import React, { useState, useEffect, useRef } from 'react';
import {
  Truck,
  X,
  Camera,
  Check,
  AlertOctagon,
  CheckCircle2,
  ChevronRight,
  FileText,
  ArrowRight,
  WifiOff,
  Image as ImageIcon,
  Trash2,
  Search,
  Plus,
  Tag,
  FolderKanban,
  Scissors,
  ShieldAlert,
  Loader2,
  Package,
  AlertTriangle,
  RefreshCw,
  Printer,
  Scale,
  History
} from 'lucide-react';
import { OABLineItem, OrderReference, OABSubOrder, computeOABStoresSummary } from '../types/oab';
import { OrderSearchModal } from './OrderSearchModal';
import { InventoryItem } from '../types/inventory';
import {
  registerReception,
  uploadEvidenceToR2,
  fetchPendingOABs,
  fetchOABDetails,
  RegisterReceptionPayload,
  ReceptionBatchProgress
} from '../services/oabService';
import { 
  queueOfflineReception, 
  uploadEvidenceWithBackoff, 
  queuePhotoForRetry, 
  getPendingPhotosCount 
} from '../services/offlineReceptionStorage';
import { compressImageFile } from '../utils/imageCompressor';
import { PrintSheetProjectLabels, ProjectLabelItem } from './PrintSheetProjectLabels';
import { BultoBreakdownDrawer } from './BultoBreakdownDrawer';
import { PrintSheetRampEntry } from './PrintSheetRampEntry';
import { PrintSheetRampRejection } from './PrintSheetRampRejection';
import { UomConversionModal } from './UomConversionModal';
import { RampSupervisorAuthModal, SupervisorAuthData } from './RampSupervisorAuthModal';
import { RampBalanceWizardModal } from './RampBalanceWizardModal';
import { RampReceptionHistoryView } from './RampReceptionHistoryView';
import { ReceptionSuccessNriPanel } from './ReceptionSuccessNriPanel';

interface ReceptionTerminalModalProps {
  isOpen: boolean;
  onClose: () => void;
  inventoryItems: InventoryItem[];
  initialSearchTerm?: string;
  initialNriFolio?: string | null;
  onReceptionSuccess?: (receivedLines?: OABLineItem[]) => void;
  onRequestRequote?: (items: OABLineItem[], originalFolio: string) => void;
}

export const ReceptionTerminalModal: React.FC<ReceptionTerminalModalProps> = ({
  isOpen,
  onClose,
  inventoryItems,
  initialSearchTerm,
  initialNriFolio,
  onReceptionSuccess,
  onRequestRequote
}) => {
  const [activeTerminalTab, setActiveTerminalTab] = useState<'RECEIVE' | 'HISTORY'>('RECEIVE');
  const [historyPrintTarget, setHistoryPrintTarget] = useState<any | null>(null);
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);
  const [selectedFolio, setSelectedFolio] = useState('');
  const [selectedOabId, setSelectedOabId] = useState<string | undefined>(undefined);
  const [manualFolioInput, setManualFolioInput] = useState('');
  const [notaEntrega, setNotaEntrega] = useState('');
  const [fechaRecepcion, setFechaRecepcion] = useState(new Date().toISOString().split('T')[0]);
  const [tasaBCV, setTasaBCV] = useState<number>(45.0);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [itemsToReceive, setItemsToReceive] = useState<OABLineItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [receptionComplete, setReceptionComplete] = useState<any | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [completedLines, setCompletedLines] = useState<OABLineItem[]>([]);
  const [showCompletedAccordion, setShowCompletedAccordion] = useState<boolean>(false);
  const [canceledLines, setCanceledLines] = useState<OABLineItem[]>([]);
  const [showCanceledAccordion, setShowCanceledAccordion] = useState<boolean>(false);
  const [batchProgress, setBatchProgress] = useState<ReceptionBatchProgress | null>(null);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [searchItemQuery, setSearchItemQuery] = useState('');

  // Selector Trimodal de Documento & Gobernanza de Rampa (PLAN-040)
  const [tipoDocumento, setTipoDocumento] = useState<'NOTA_ENTREGA' | 'SENIAT' | 'FLETE_INTERNO' | 'SN'>('NOTA_ENTREGA');
  const [motivoBypassFoto, setMotivoBypassFoto] = useState<string>('');
  const [showBypassPrompt, setShowBypassPrompt] = useState<boolean>(false);
  const [targetCuadrilla, setTargetCuadrilla] = useState<string>('ALMACEN');

  // Asistente de Saldo Tripartito & Gobernanza MTO (PLAN-040)
  const [showBalanceWizardModal, setShowBalanceWizardModal] = useState<boolean>(false);
  const [balanceWizardConfirmed, setBalanceWizardConfirmed] = useState<boolean>(false);

  // Impresiones Bimodales de Rampa (PLAN-040 / PLAN-046 / PLAN-048)
  const [showPrintEntryModal, setShowPrintEntryModal] = useState<boolean>(false);
  const [showPrintRejectionModal, setShowPrintRejectionModal] = useState<boolean>(false);
  const [nriPrinted, setNriPrinted] = useState<boolean>(false);
  const [rejectionPrinted, setRejectionPrinted] = useState<boolean>(false);
  const [choferNombre, setChoferNombre] = useState<string>('');
  const [placaVehiculo, setPlacaVehiculo] = useState<string>('');
  const [selectedOabStoresSummary, setSelectedOabStoresSummary] = useState<string>('');

  // Asistente de Conversión de Unidades UoM y Tasa Dinámica BCV (PLAN-042)
  const [uomModalItemIdx, setUomModalItemIdx] = useState<number | null>(null);
  const [liveBcvRate, setLiveBcvRate] = useState<number | null>(null);

  // Estados de Desacople Multiproveedor y Rampa Bimodal (PLAN-039 / Decisiones D4 y D5)
  const [subOrdenesList, setSubOrdenesList] = useState<OABSubOrder[]>([]);
  const [activeSubOrderFolio, setActiveSubOrderFolio] = useState<string | null>(null);

  // Estados de Facturación Fiscal SENIAT y Resiliencia R2 (Fase 9C / 9D)
  const [numeroFacturaFiscal, setNumeroFacturaFiscal] = useState('');
  const [numeroControlFiscal, setNumeroControlFiscal] = useState('');
  const [showFiscalInputs, setShowFiscalInputs] = useState(false);
  const [pendingPhotosCount, setPendingPhotosCount] = useState<number>(0);

  // Estados de Rotulado Físico MTO y Etiquetas Duales (Fase 10B / Fase 11.3)
  const [labelsToPrint, setLabelsToPrint] = useState<ProjectLabelItem[]>([]);
  const [showLabelModal, setShowLabelModal] = useState<boolean>(false);
  const [activeLineIdxForOrder, setActiveLineIdxForOrder] = useState<number | null>(null);
  const [isOrderSearchOpen, setIsOrderSearchOpen] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [activeDrawerItemIdx, setActiveDrawerItemIdx] = useState<number | null>(null);

  // Estados para Rectificación y Autorización PIN de Supervisor (PLAN-012 / PLAN-043)
  const [showSupervisorAuthModal, setShowSupervisorAuthModal] = useState<boolean>(false);
  const [verifiedSupervisorData, setVerifiedSupervisorData] = useState<SupervisorAuthData | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Insumos filtrados para búsqueda y adición directa en rampa
  const filteredInventory = searchItemQuery.trim()
    ? inventoryItems.filter(item =>
        item.nombre.toLowerCase().includes(searchItemQuery.toLowerCase()) ||
        (item.codigo && item.codigo.toLowerCase().includes(searchItemQuery.toLowerCase())) ||
        (item.categoriaMaterial && item.categoriaMaterial.toLowerCase().includes(searchItemQuery.toLowerCase()))
      ).slice(0, 8)
    : [];

  // Monitorear conectividad online/offline
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Monitorear cola de fotos diferidas y eventos de sincronización
  useEffect(() => {
    getPendingPhotosCount().then(setPendingPhotosCount).catch(() => {});
    const handleSync = (e: any) => {
      if (e.detail?.photoCount !== undefined) {
        setPendingPhotosCount(e.detail.photoCount);
      }
    };
    window.addEventListener('rampa-sync-updated', handleSync);
    return () => window.removeEventListener('rampa-sync-updated', handleSync);
  }, []);

  // Cargar lista de OABs pendientes al abrir el modal
  useEffect(() => {
    if (!isOpen) return;

    setSelectedFolio('');
    setSelectedOabId(undefined);
    setSubOrdenesList([]);
    setActiveSubOrderFolio(null);
    setManualFolioInput('');
    setNotaEntrega('');
    setNumeroFacturaFiscal('');
    setNumeroControlFiscal('');
    setShowFiscalInputs(false);
    setFechaRecepcion(new Date().toISOString().split('T')[0]);
    setPhotoPreview(null);
    setReceptionComplete(null);
    setErrorMessage(null);
    setValidationError(null);
    setCompletedLines([]);
    setShowCompletedAccordion(false);
    setCanceledLines([]);
    setShowCanceledAccordion(false);
    setBatchProgress(null);
    setSearchItemQuery(initialSearchTerm || '');
    setItemsToReceive([]);
    setLabelsToPrint([]);
    setShowLabelModal(false);
    setTipoDocumento('NOTA_ENTREGA');
    setMotivoBypassFoto('');
    setShowBypassPrompt(false);
    setTargetCuadrilla('ALMACEN');
    setShowBalanceWizardModal(false);
    setBalanceWizardConfirmed(false);
    setShowPrintEntryModal(false);
    setShowPrintRejectionModal(false);
    setHistoryPrintTarget(null);
    setUomModalItemIdx(null);

    if (initialNriFolio || (initialSearchTerm && (initialSearchTerm.startsWith('NRI-') || initialSearchTerm.startsWith('RECHAZO-')))) {
      setActiveTerminalTab('HISTORY');
    } else {
      setActiveTerminalTab('RECEIVE');
    }

    // Consultar tasa oficial BCV del día en caliente (PLAN-042 / GAP-041-01)
    fetch('/api/bcv/rate')
      .then(r => r.json())
      .then(data => {
        if (data && typeof data.rate === 'number' && data.rate > 0) {
          setLiveBcvRate(data.rate);
        }
      })
      .catch(err => console.warn('Error consultando tasa BCV en rampa:', err));

    // Cargar órdenes pendientes desde Notion
    fetchPendingOABs().then(orders => {
      // Priorizar órdenes en tránsito real ('En Compra' o 'Recepción Parcial')
      const rampaOrders = orders.filter(o => o.estadoGeneral === 'En Compra' || o.estadoGeneral === 'Recepción Parcial');
      const targetList = rampaOrders.length > 0 ? rampaOrders : orders;
      setPendingOrders(targetList);
      if (initialSearchTerm && initialSearchTerm.startsWith('OAB-')) {
        handleLoadOAB(initialSearchTerm);
      } else if (targetList.length > 0) {
        handleLoadOAB(targetList[0].folio);
      } else {
        setSelectedFolio('');
        setSelectedOabId(undefined);
        setItemsToReceive([]);
      }
    }).catch(() => {
      setSelectedFolio('');
      setSelectedOabId(undefined);
    });
    setShowSupervisorAuthModal(false);
    setVerifiedSupervisorData(null);
  }, [isOpen, initialNriFolio, initialSearchTerm]);

  // Funciones de Rectificación de Destinos y Split en Rampa (PLAN-012)
  const isLineRectified = (item: OABLineItem): boolean => {
    if (item.isSplitChild) return true;
    const orig = (item.originalProyectoNombre || '').trim().toLowerCase();
    const curr = (item.proyectoNombre || '').trim().toLowerCase();
    const isOrigStk = !orig || orig.includes('stock general') || orig.includes('stock fábrica') || orig.includes('stock fabrica');
    const isCurrStk = !curr || curr.includes('stock general') || curr.includes('stock fábrica') || curr.includes('stock fabrica');
    if (isOrigStk && isCurrStk) return false;
    return orig !== curr;
  };

  const rectifiedItems = itemsToReceive.filter(isLineRectified);
  const hasRectifications = rectifiedItems.length > 0;

  // Fraccionar destino de una línea recibida (PLAN-012)
  const handleSplitDestination = (idx: number) => {
    const item = itemsToReceive[idx];
    const currentQty = item.cantidadRecibidaHoy ?? item.cantidadRecibida ?? 1;
    if (currentQty <= 1) {
      alert(`Para dividir el destino de "${item.nombre}", la cantidad recibida debe ser mayor a 1 (actual: ${currentQty}).`);
      return;
    }

    const half = Math.floor(currentQty / 2);
    const splitQtyInput = prompt(
      `Dividir destino para: ${item.nombre}\nCantidad actual recibida: ${currentQty} ${item.unidadMedida || 'und'}\n\nIndica cuántas unidades separar para el nuevo renglón hijo:`,
      String(half)
    );

    if (!splitQtyInput) return;
    const splitQty = parseInt(splitQtyInput, 10);
    if (isNaN(splitQty) || splitQty <= 0 || splitQty >= currentQty) {
      alert(`La cantidad a separar debe ser un número entero entre 1 y ${currentQty - 1}.`);
      return;
    }

    const remainingQty = currentQty - splitQty;
    const rootId = item.parentSolicitudId || (item.id ? item.id.split(/_(?:split|child)_/)[0] : '');
    const origApproved = item.originalCantidadAprobada ?? item.cantidadAprobada ?? item.cantidadSolicitada ?? currentQty;
    const currentApproved = item.cantidadAprobada ?? item.cantidadSolicitada ?? currentQty;
    const parentShareApproved = Math.max(remainingQty, currentApproved - splitQty);
    const parentPrevRec = item.cantidadRecibidaPrevia || 0;
    const parentPendingAfterSplit = Math.max(0, parentShareApproved - parentPrevRec);
    const parentBackorder = Math.max(0, parentPendingAfterSplit - remainingQty);

    const existingSiblingSplits = itemsToReceive.filter(sib => {
      const sibRoot = sib.parentSolicitudId || (sib.id ? sib.id.split(/_(?:split|child)_/)[0] : '');
      return Boolean(sib.isSplitChild && rootId && sibRoot === rootId);
    });
    const nextSplitIndex = existingSiblingSplits.length + 1;
    const deterministicChildId = `${rootId || item.id || 'split'}_split_${nextSplitIndex}`;

    const updatedParent: OABLineItem = {
      ...item,
      parentSolicitudId: rootId || item.id,
      originalCantidadAprobada: origApproved,
      cantidadAprobada: parentShareApproved,
      cantidadSolicitada: Math.min(item.cantidadSolicitada || parentShareApproved, parentShareApproved),
      cantidadRecibida: remainingQty,
      cantidadRecibidaHoy: remainingQty,
      backorderPendiente: parentBackorder,
      cantEnBulto: Math.max(1, Math.min(item.cantEnBulto || remainingQty, remainingQty)),
      desgloseBultos: [remainingQty],
      subtotalUSD: remainingQty * (item.costoUnitarioUSD || 0)
    };

    const newChild: OABLineItem = {
      ...item,
      id: deterministicChildId,
      parentSolicitudId: rootId || item.id,
      originalCantidadAprobada: origApproved,
      cantidadRecibida: splitQty,
      cantidadRecibidaHoy: splitQty,
      cantidadRecibidaPrevia: 0,
      cantidadAprobada: splitQty,
      cantidadSolicitada: splitQty,
      backorderPendiente: 0,
      bultos: 1,
      cantEnBulto: splitQty,
      desgloseBultos: [splitQty],
      subtotalUSD: splitQty * (item.costoUnitarioUSD || 0),
      isSplitChild: true,
      originalProyectoNombre: item.originalProyectoNombre || item.proyectoNombre,
      originalPedidoId: item.originalPedidoId || item.pedidoId,
      pedidoId: undefined,
      proyectoId: undefined,
      proyectoNombre: undefined,
      rotularEtiqueta: true
    };

    setItemsToReceive(prev => {
      const copy = [...prev];
      copy[idx] = updatedParent;
      copy.splice(idx + 1, 0, newChild);
      return copy;
    });

    setActiveLineIdxForOrder(idx + 1);
    setIsOrderSearchOpen(true);
  };

  // Comprobar si una línea pertenece a una sub-orden específica (PLAN-039 / D4)
  const isLineOfSubOrder = (line: OABLineItem, subOrder: OABSubOrder): boolean => {
    if (line.subOrdenFolio && subOrder.folio) {
      if (line.subOrdenFolio.trim().toUpperCase() === subOrder.folio.trim().toUpperCase()) return true;
    }
    if (line.subOrdenId && subOrder.id) {
      if (line.subOrdenId === subOrder.id) return true;
    }
    const lineProv = (line.proveedorAdjudicado || line.proveedorNombre || line.proveedor || '').trim().toLowerCase();
    const subProv = (subOrder.proveedor || '').trim().toLowerCase();
    if (lineProv && subProv && lineProv === subProv) {
      return true;
    }
    return false;
  };

  const activeSubOrder = subOrdenesList.find(s => s.folio === activeSubOrderFolio) || null;
  const visibleItems = activeSubOrder ? itemsToReceive.filter(l => isLineOfSubOrder(l, activeSubOrder)) : itemsToReceive;

  const handleSelectSubOrderTab = (targetFolio: string | null) => {
    setActiveSubOrderFolio(targetFolio);
    if (targetFolio) {
      const sub = subOrdenesList.find(s => s.folio === targetFolio);
      if (sub?.numeroCotizacion && (!notaEntrega || notaEntrega === 'S/N')) {
        setNotaEntrega(sub.numeroCotizacion);
      }
    }
  };

  const handleSelectOAB = async (folio: string) => {
    setSelectedFolio(folio);
    if (!folio) {
      setSelectedOabId(undefined);
      setSubOrdenesList([]);
      setActiveSubOrderFolio(null);
      setItemsToReceive([]);
      return;
    }
    handleLoadOAB(folio);
  };

  const handleAddItem = (item: InventoryItem) => {
    setItemsToReceive(prev => {
      const existingIdx = prev.findIndex(i =>
        (i.dashboardId && i.dashboardId === item.id) ||
        (i.insumoId && i.insumoId === item.insumoId) ||
        (i.nombre.toLowerCase() === item.nombre.toLowerCase())
      );

      if (existingIdx >= 0) {
        const copy = [...prev];
        const currentQty = copy[existingIdx].cantidadRecibida || 0;
        copy[existingIdx] = {
          ...copy[existingIdx],
          cantidadRecibida: currentQty + 1
        };
        return copy;
      }

      const newLine: OABLineItem = {
        nombre: item.nombre,
        insumoId: item.insumoId || item.id,
        dashboardId: item.id,
        proveedor: activeSubOrder?.proveedor || '',
        proveedorAdjudicado: activeSubOrder?.proveedor || '',
        subOrdenFolio: activeSubOrder?.folio || null,
        subOrdenId: activeSubOrder?.id || null,
        cantidadStock: item.stockBase || 0,
        stockMinimo: item.stockMinimo || 0,
        deficit: item.deficit || 0,
        cantidadSugerida: 1,
        cantidadSolicitada: 1,
        cantidadAprobada: 0,
        cantidadRecibida: 1,
        cantidadRechazada: 0,
        backorderPendiente: 0,
        costoUnitarioUSD: item.costoUnitarioUSD || 0,
        costoAprobadoUSD: item.costoUnitarioUSD || 0,
        subtotalUSD: item.costoUnitarioUSD || 0,
        prioridad: item.prioridad || 'Media',
        codigo: item.codigo,
        categoriaMaterial: item.categoriaMaterial,
        rotularEtiqueta: true,
        bultos: 1,
        cantEnBulto: 1
      };
      return [newLine, ...prev];
    });
    setSearchItemQuery('');
  };

  const handleRemoveItem = (indexToRemove: number) => {
    setItemsToReceive(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleLoadOAB = async (folio: string) => {
    if (!folio) return;
    setSelectedFolio(folio);
    setIsSearching(true);
    setErrorMessage(null);
    setValidationError(null);

    try {
      const data = await fetchOABDetails(folio);
      if (data) {
        setSelectedOabId(data.oab.id);
        setSelectedOabStoresSummary(data.oab.resumenTiendasClientes || '');
        setNriPrinted(false);
        setRejectionPrinted(false);
        if (data.oab.cotizacion) {
          setNotaEntrega(prev => prev || data.oab.cotizacion || '');
        }
        if (data.oab.tasaBCV) {
          setTasaBCV(data.oab.tasaBCV);
        }

        // Gestión de Sub-Órdenes Bifurcadas (PLAN-039 / D4)
        const subs = (data.oab.subOrdenes || []) as OABSubOrder[];
        setSubOrdenesList(subs);
        if (subs.length > 0) {
          // Pre-seleccionar la primera sub-orden que aún tenga recepción pendiente
          const firstPending = subs.find(s => s.estadoGeneral !== 'Completada' && s.estadoGeneral !== 'Recibido Completo');
          const defaultSub = firstPending || subs[0];
          setActiveSubOrderFolio(defaultSub.folio);
          if (defaultSub.numeroCotizacion && (!notaEntrega || notaEntrega === 'S/N')) {
            setNotaEntrega(defaultSub.numeroCotizacion);
          }
        } else {
          setActiveSubOrderFolio(null);
        }

        const pendingList: OABLineItem[] = [];
        const finishedList: OABLineItem[] = [];
        const canceledList: OABLineItem[] = [];

        data.lines.forEach(line => {
          const isCanceledByMgmt = Boolean(
            line.isRejected ||
            line.estadoFlujo === 'Cancelada' ||
            line.estadoFlujo === 'Rechazada' ||
            (line.isReviewed && (line.cantidadAprobada ?? 0) <= 0)
          );
          const approved = isCanceledByMgmt
            ? 0
            : (line.cantidadAprobada ?? line.cantidadSolicitada ?? 0);
          const prevReceived = line.cantidadRecibidaPrevia ?? 0;
          const pendingBalance = isCanceledByMgmt
            ? 0
            : (line.backorderPendiente !== undefined
              ? line.backorderPendiente
              : Math.max(0, approved - prevReceived));

          const rawProv = line.proveedorAdjudicado || line.proveedorNombre || line.proveedor || '';

          // Cruce con catálogo en memoria para recuperar SKU (codigo), unidad de medida y categoría (PLAN-048)
          const catalogMatch = inventoryItems.find((inv: any) =>
            (line.dashboardId && (inv.id === line.dashboardId || inv.notionPageId === line.dashboardId)) ||
            (line.insumoId && (inv.id === line.insumoId || inv.notionPageId === line.insumoId)) ||
            inv.nombre.trim().toLowerCase() === (line.nombre || '').trim().toLowerCase()
          ) as any;

          const resolvedCodigo = line.codigo || catalogMatch?.codigo || undefined;
          const resolvedUnidad =
            line.unidadMedida ||
            catalogMatch?.unidadMedida ||
            catalogMatch?.uom ||
            catalogMatch?.unidad ||
            'und';

          const isDirectOrService = Boolean(
            line.isDirectOrService ||
            line.rutaLogistica === 'DIRECTO_OBRA' ||
            line.rutaLogistica === 'SERVICIO_SITIO'
          );

          const itemObj: OABLineItem = {
            id: line.solicitudId,
            nombre: line.nombre,
            codigo: resolvedCodigo,
            unidadMedida: resolvedUnidad,
            categoriaMaterial: catalogMatch?.categoriaMaterial,
            insumoId: line.insumoId || catalogMatch?.notionPageId || catalogMatch?.id,
            dashboardId: line.dashboardId || catalogMatch?.id,
            proveedor: rawProv,
            proveedorAdjudicado: rawProv,
            subOrdenFolio: line.subOrdenFolio || null,
            subOrdenId: line.subOrdenId || null,
            cantidadStock: catalogMatch?.cantidadStock ?? catalogMatch?.stockActual ?? 0,
            stockMinimo: catalogMatch?.stockMinimo ?? 0,
            deficit: 0,
            cantidadSugerida: line.cantidadSolicitada,
            cantidadSolicitada: line.cantidadSolicitada,
            cantidadAprobada: approved,
            cantidadRecibidaPrevia: prevReceived,
            cantidadRecibida: pendingBalance,
            cantidadRecibidaHoy: pendingBalance,
            cantidadRechazada: 0,
            backorderPendiente: 0,
            costoUnitarioUSD: line.costoUnitarioUSD,
            costoAprobadoUSD: line.costoUnitarioUSD,
            subtotalUSD: line.subtotalUSD,
            prioridad: line.prioridad || 'Alta',
            pedidoId: line.pedidoId,
            proyectoId: line.proyectoId,
            proyectoNombre: line.proyectoNombre,
            tiendaNombre: line.tiendaNombre,
            clienteNombre: line.clienteNombre,
            originalPedidoId: line.pedidoId,
            originalProyectoNombre: line.proyectoNombre,
            rutaLogistica: line.rutaLogistica || 'VIA_GALPON',
            orderLineId: line.orderLineId || null,
            isDirectOrService,
            rotularEtiqueta: !isDirectOrService,
            bultos: 1,
            cantEnBulto: pendingBalance > 0 ? pendingBalance : 1
          };

          if (isCanceledByMgmt) {
            canceledList.push(itemObj);
          } else if (pendingBalance > 0) {
            pendingList.push(itemObj);
          } else if (approved > 0) {
            finishedList.push(itemObj);
          }
        });

        setItemsToReceive(pendingList);
        setCompletedLines(finishedList);
        setCanceledLines(canceledList);
      }
    } catch (err: any) {
      console.warn('No se pudo cargar detalle de OAB, usando fallback:', err);
      setSelectedOabId(undefined);
    } finally {
      setIsSearching(false);
    }
  };

  // Captura fotográfica con compresión Canvas pericial (máx 1600px, JPEG 80%)
  const handlePhotoCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const compressed = await compressImageFile(file, 1600, 0.8);
        setPhotoPreview(compressed);
      } catch (err) {
        console.warn('Fallo compresión pericial en cliente, usando lector nativo:', err);
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64 = event.target?.result as string;
          setPhotoPreview(base64);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // Touch helpers para rampa (PLAN-055: Nullish coalescing estricto ??)
  const handleSetFull = (idx: number) => {
    setItemsToReceive(prev => {
      const copy = [...prev];
      const approved = copy[idx].cantidadAprobada ?? copy[idx].cantidadSolicitada ?? 0;
      const prevRec = copy[idx].cantidadRecibidaPrevia || 0;
      const pending = Math.max(0, approved - prevRec);
      const bultos = copy[idx].bultos || 1;
      copy[idx] = {
        ...copy[idx],
        cantidadRecibida: pending,
        cantidadRecibidaHoy: pending,
        cantidadRechazada: 0,
        backorderPendiente: 0,
        cantEnBulto: Math.max(1, Math.ceil(pending / bultos))
      };
      return copy;
    });
  };

  const handleSetNextFreight = (idx: number) => {
    setItemsToReceive(prev => {
      const copy = [...prev];
      const approved = copy[idx].cantidadAprobada ?? copy[idx].cantidadSolicitada ?? 0;
      const prevRec = copy[idx].cantidadRecibidaPrevia || 0;
      const pending = Math.max(0, approved - prevRec);
      copy[idx] = {
        ...copy[idx],
        cantidadRecibida: 0,
        cantidadRecibidaHoy: 0,
        cantidadRechazada: 0,
        backorderPendiente: pending
      };
      return copy;
    });
  };

  const handleSetAllRemainingAsNextFreight = () => {
    setBalanceWizardConfirmed(false);
    setItemsToReceive(prev => {
      return prev.map(item => {
        if (activeSubOrder && !isLineOfSubOrder(item, activeSubOrder)) {
          return item;
        }
        const approved = item.cantidadAprobada ?? item.cantidadSolicitada ?? 0;
        const prevRec = item.cantidadRecibidaPrevia || 0;
        const pending = Math.max(0, approved - prevRec);
        return {
          ...item,
          cantidadRecibida: 0,
          cantidadRecibidaHoy: 0,
          cantidadRechazada: 0,
          backorderPendiente: pending
        };
      });
    });
  };

  const handleSetAllFull = () => {
    setBalanceWizardConfirmed(false);
    setItemsToReceive(prev => {
      return prev.map(item => {
        if (activeSubOrder && !isLineOfSubOrder(item, activeSubOrder)) {
          return item;
        }
        const approved = item.cantidadAprobada ?? item.cantidadSolicitada ?? 0;
        const prevRec = item.cantidadRecibidaPrevia || 0;
        const pending = Math.max(0, approved - prevRec);
        const bultos = item.bultos || 1;
        return {
          ...item,
          cantidadRecibida: pending,
          cantidadRecibidaHoy: pending,
          cantidadRechazada: 0,
          backorderPendiente: 0,
          cantEnBulto: Math.max(1, Math.ceil(pending / bultos)),
          actionOnRemaining: undefined,
          motivoSaldo: undefined
        };
      });
    });
  };

  const handleSetZero = (idx: number) => {
    setItemsToReceive(prev => {
      const copy = [...prev];
      const approved = copy[idx].cantidadAprobada ?? copy[idx].cantidadSolicitada ?? 0;
      const prevRec = copy[idx].cantidadRecibidaPrevia || 0;
      const pending = Math.max(0, approved - prevRec);
      copy[idx] = {
        ...copy[idx],
        cantidadRecibida: 0,
        cantidadRecibidaHoy: 0,
        cantidadRechazada: pending,
        backorderPendiente: pending
      };
      return copy;
    });
  };

  const handleQuantityChange = (idx: number, field: 'cantidadRecibida' | 'cantidadRechazada' | 'costoUnitarioUSD', value: number) => {
    setItemsToReceive(prev => {
      const copy = [...prev];
      const val = Math.max(0, value);
      copy[idx] = {
        ...copy[idx],
        [field]: val
      };

      if (field === 'cantidadRecibida') {
        copy[idx].cantidadRecibidaHoy = val;
        const approved = copy[idx].cantidadAprobada ?? copy[idx].cantidadSolicitada ?? 0;
        const prevRec = copy[idx].cantidadRecibidaPrevia || 0;
        const pending = Math.max(0, approved - prevRec);
        copy[idx].backorderPendiente = Math.max(0, pending - val);
        const bultos = copy[idx].bultos || 1;
        copy[idx].cantEnBulto = Math.max(1, Math.ceil(val / bultos));
        if (val >= pending) {
          copy[idx].actionOnRemaining = undefined;
          copy[idx].motivoSaldo = undefined;
        }
      }

      return copy;
    });
  };

  // Asistente de Conversión de Unidades UoM en Muelle (PLAN-042 / GAP-041-02)
  const handleOpenUomModal = (idx: number) => {
    setUomModalItemIdx(idx);
  };

  const handleApplyUomConversion = (pieces: number, proofNote: string) => {
    if (uomModalItemIdx === null) return;
    setItemsToReceive(prev => {
      const copy = [...prev];
      const target = copy[uomModalItemIdx];
      const approved = target.cantidadAprobada ?? target.cantidadSolicitada ?? 0;
      const prevRec = target.cantidadRecibidaPrevia || 0;
      const pending = Math.max(0, approved - prevRec);

      const existingNotes = target.notasDiscrepancia || '';
      const updatedNotes = existingNotes ? `${existingNotes} · ${proofNote}` : proofNote;

      const bultos = target.bultos || 1;
      copy[uomModalItemIdx] = {
        ...target,
        cantidadRecibida: pieces,
        cantidadRecibidaHoy: pieces,
        subtotalUSD: pieces * (target.costoUnitarioUSD || 0),
        backorderPendiente: Math.max(0, pending - pieces),
        cantEnBulto: Math.max(1, Math.ceil(pieces / bultos)),
        notasDiscrepancia: updatedNotes
      };
      return copy;
    });
    setUomModalItemIdx(null);
  };

  // Helper para asignar acción global de saldo en rampa (PLAN-040)
  const handleApplyGlobalBalanceAction = (action: 'BACKORDER' | 'DISCARD' | 'REQUOTE_ANOTHER_VENDOR') => {
    setItemsToReceive(prev => prev.map(item => {
      const approved = item.cantidadAprobada ?? item.cantidadSolicitada ?? 0;
      const prevRec = item.cantidadRecibidaPrevia || 0;
      const pending = Math.max(0, approved - prevRec);
      const recHoy = item.cantidadRecibidaHoy !== undefined ? item.cantidadRecibidaHoy : (item.cantidadRecibida || 0);
      if (pending > 0 && recHoy < pending) {
        return {
          ...item,
          actionOnRemaining: action,
          motivoSaldo: action === 'BACKORDER' ? 'Backorder: próximo flete' :
                       action === 'DISCARD' ? 'Descarte definitivo por cierre' :
                       'Quiebre de stock: re-cotizar con otro proveedor'
        };
      }
      return item;
    }));
  };

  const handleSetItemBalanceAction = (idx: number, action: 'BACKORDER' | 'DISCARD' | 'REQUOTE_ANOTHER_VENDOR') => {
    setItemsToReceive(prev => {
      const target = prev[idx];
      if (!target) return prev;
      const targetRootKey = target.parentSolicitudId || (target.id ? target.id.split(/_(?:split|child)_/)[0] : '');
      const motivo = action === 'BACKORDER' ? 'Backorder: próximo flete' :
                     action === 'DISCARD' ? 'Descarte definitivo por cierre' :
                     'Quiebre de stock: re-cotizar con otro proveedor';
      return prev.map((it, i) => {
        const itRootKey = it.parentSolicitudId || (it.id ? it.id.split(/_(?:split|child)_/)[0] : '');
        if (i === idx || (targetRootKey && itRootKey === targetRootKey)) {
          return {
            ...it,
            actionOnRemaining: action,
            motivoSaldo: motivo
          };
        }
        return it;
      });
    });
  };

  // Handlers ergonómicos de rotulado y bultos (Micro-Fase 10B / PLAN-012)
  const handleToggleRotulado = (idx: number) => {
    setItemsToReceive(prev => {
      const copy = [...prev];
      const current = copy[idx].rotularEtiqueta !== false;
      copy[idx] = { ...copy[idx], rotularEtiqueta: !current };
      return copy;
    });
  };

  const handleBultosChange = (idx: number, bultosVal: number) => {
    setItemsToReceive(prev => {
      const copy = [...prev];
      const item = copy[idx];
      const received = item.cantidadRecibidaHoy ?? item.cantidadRecibida ?? 1;
      const safeBultos = Math.max(1, bultosVal);
      copy[idx] = {
        ...item,
        bultos: safeBultos,
        cantEnBulto: Math.max(1, Math.ceil(received / safeBultos)),
        desgloseBultos: undefined
      };
      return copy;
    });
  };

  const handleCantEnBultoChange = (idx: number, cantVal: number) => {
    setItemsToReceive(prev => {
      const copy = [...prev];
      copy[idx] = { 
        ...copy[idx], 
        cantEnBulto: Math.max(1, cantVal),
        desgloseBultos: undefined 
      };
      return copy;
    });
  };

  const handleResetLoteCompleto = (idx: number) => {
    setItemsToReceive(prev => {
      const copy = [...prev];
      const item = copy[idx];
      const received = item.cantidadRecibidaHoy ?? item.cantidadRecibida ?? 1;
      copy[idx] = {
        ...item,
        bultos: 1,
        cantEnBulto: Math.max(1, received),
        desgloseBultos: undefined
      };
      return copy;
    });
  };

  const handleOpenBreakdownDrawer = (idx: number) => {
    setActiveDrawerItemIdx(idx);
    setDrawerOpen(true);
  };

  const handleApplyDesglose = (desglose: number[]) => {
    if (activeDrawerItemIdx === null) return;
    setItemsToReceive(prev => {
      const copy = [...prev];
      const item = copy[activeDrawerItemIdx];
      copy[activeDrawerItemIdx] = {
        ...item,
        desgloseBultos: desglose,
        bultos: desglose.length,
        cantEnBulto: desglose[0] || item.cantEnBulto || 1
      };
      return copy;
    });
  };

  const handleToggleTolerance = (idx: number) => {
    setItemsToReceive(prev => {
      const copy = [...prev];
      copy[idx] = {
        ...copy[idx],
        toleranciaExcedente: !copy[idx].toleranciaExcedente
      };
      return copy;
    });
  };

  const handleNotesChange = (idx: number, text: string) => {
    setItemsToReceive(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], notasDiscrepancia: text };
      return copy;
    });
  };

  // Helper para construir la lista de etiquetas a partir de los ítems actuales (Rotulado Universal PLAN-012)
  const buildLabelsFromCurrentItems = (itemsList?: OABLineItem[]): ProjectLabelItem[] => {
    const activeSubOrder = subOrdenesList.find(s => s.folio === activeSubOrderFolio) || null;
    const activeFolio = activeSubOrder ? activeSubOrder.folio : (selectedFolio || manualFolioInput || `RAMPA-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`);
    const sourceItems = itemsList || visibleItems;
    const generatedLabels: ProjectLabelItem[] = [];
    sourceItems.forEach(l => {
      const isRotular = l.rotularEtiqueta !== false;
      const receivedToday = l.cantidadRecibidaHoy ?? l.cantidadRecibida ?? 0;
      if (isRotular && receivedToday > 0) {
        const approved = l.cantidadAprobada ?? l.cantidadSolicitada ?? receivedToday;
        const prev = l.cantidadRecibidaPrevia || 0;
        const totalCum = prev + receivedToday;
        const isParcial = totalCum < approved;
        const pending = Math.max(0, approved - totalCum);
        const bultos = Math.max(1, l.bultos || 1);
        const cantEnBulto = l.cantEnBulto || Math.ceil(receivedToday / bultos);

        generatedLabels.push({
          id: l.id || `lbl_${l.dashboardId || l.nombre}`,
          insumoId: l.insumoId,
          dashboardId: l.dashboardId,
          nombre: l.nombre,
          codigo: l.codigo,
          categoria: l.categoriaMaterial,
          folioOAB: activeFolio,
          fechaRecepcion,
          proyectoNombre: l.proyectoNombre || '📦 STOCK GENERAL / FÁBRICA',
          cantidadRecibidaHoy: receivedToday,
          cantidadTotalAprobada: approved,
          backorderPendiente: pending,
          isParcial,
          bultos,
          cantEnBulto,
          desgloseBultos: l.desgloseBultos,
          unidadMedida: l.unidadMedida || 'und'
        });
      }
    });
    return generatedLabels;
  };

  const handlePreviewLabels = () => {
    const labels = buildLabelsFromCurrentItems();
    if (labels.length === 0) {
      setValidationError('No hay materiales con cantidad mayor a 0 marcados para rotulado de etiquetas.');
      return;
    }
    setValidationError(null);
    setLabelsToPrint(labels);
    setShowLabelModal(true);
  };

  // Ejecución atómica de confirmación de recepción con tolerancia a fallas y soporte offline
  const executeConfirmReception = async (supervisorAuthData?: SupervisorAuthData) => {
    setIsProcessing(true);
    setErrorMessage(null);

    const activeSubOrder = subOrdenesList.find(s => s.folio === activeSubOrderFolio) || null;
    const activeFolio = activeSubOrder ? activeSubOrder.folio : (selectedFolio || manualFolioInput || `RAMPA-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`);
    const targetOabId = activeSubOrder ? activeSubOrder.id : selectedOabId;
    const linesToProcess = activeSubOrder ? itemsToReceive.filter(l => isLineOfSubOrder(l, activeSubOrder)) : itemsToReceive;

    if (linesToProcess.length === 0) {
      setValidationError('No hay materiales registrados para recibir en esta sub-orden o flete.');
      setIsProcessing(false);
      return;
    }

    const cleanNota = notaEntrega.trim();
    const isDeviceOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    
    // Subida pericial a Cloudflare R2 con backoff exponencial si estamos online y hay captura de foto
    let r2EvidenceUrl: string | undefined = undefined;
    let fotoPendienteSync = false;
    if (photoPreview && isDeviceOnline) {
      try {
        r2EvidenceUrl = await uploadEvidenceWithBackoff(activeFolio, cleanNota, photoPreview, 4);
      } catch (r2Err) {
        console.warn('Subida a R2 agotó reintentos, encolando foto para reintento diferido:', r2Err);
        await queuePhotoForRetry({
          folioOAB: activeFolio,
          numeroNotaEntrega: cleanNota,
          imageBase64: photoPreview,
          attempts: 4,
          lastError: String(r2Err)
        });
        fotoPendienteSync = true;
      }
    }

    const authToUse = supervisorAuthData || verifiedSupervisorData;
    const formattedSupervisorAuth = authToUse ? {
      supervisorId: authToUse.id,
      supervisorName: authToUse.name,
      supervisorPin: authToUse.supervisorPin,
      authorizedAt: new Date().toISOString()
    } : undefined;

    // Preparar etiquetas físicas universales para impresión (Micro-Fase 10B / PLAN-012)
    const generatedLabels = buildLabelsFromCurrentItems(linesToProcess);
    setLabelsToPrint(generatedLabels);

    const activeVendorName =
      subOrdenesList.find(s => s.folio === activeSubOrderFolio)?.proveedor ||
      pendingOrders.find(o => o.folio === selectedFolio)?.proveedor ||
      'Proveedor General';

    const dateCompact = (fechaRecepcion || new Date().toISOString().slice(0, 10)).replace(/-/g, '').slice(0, 8);
    const offlineSuffix = String(Date.now()).slice(-4);
    const provisionalOfflineNri = `NRI-${dateCompact}-OFF-${offlineSuffix}`;
    const hasRejectedUnits = linesToProcess.some(l => (l.cantidadRechazada || 0) > 0);

    const computedStoresSummary =
      selectedOabStoresSummary ||
      computeOABStoresSummary(linesToProcess) ||
      linesToProcess.find(it => it.proyectoNombre)?.proyectoNombre;

    const payload: RegisterReceptionPayload = {
      folioOAB: activeFolio,
      parentFolioOAB: selectedFolio || activeFolio.split('/')[0],
      oabId: targetOabId,
      proveedor: activeVendorName,
      proveedorNombre: activeVendorName,
      numeroNotaEntrega: cleanNota,
      fechaRecepcion,
      tasaBCV: Number(tasaBCV) || 1.0,
      tipoDocumento,
      motivoBypassFoto: motivoBypassFoto.trim() || undefined,
      targetCuadrilla,
      comprobanteUrl: r2EvidenceUrl,
      comprobanteFile: !r2EvidenceUrl && photoPreview && !fotoPendienteSync ? photoPreview : undefined,
      numeroFacturaFiscal: numeroFacturaFiscal.trim() || undefined,
      numeroControlFiscal: numeroControlFiscal.trim() || undefined,
      choferNombre: choferNombre.trim() || undefined,
      placaVehiculo: placaVehiculo.trim().toUpperCase() || undefined,
      resumenTiendasClientes: computedStoresSummary,
      fotoPendienteSync: fotoPendienteSync || undefined,
      offlineNriFolio: provisionalOfflineNri,
      labelsSnapshot: generatedLabels,
      supervisorAuth: formattedSupervisorAuth,
      items: linesToProcess.map(l => ({
        solicitudId: l.id,
        dashboardId: l.dashboardId,
        insumoId: l.insumoId,
        codigo: l.codigo,
        unidadMedida: l.unidadMedida || 'und',
        nombre: l.nombre,
        cantidadAprobada: l.cantidadAprobada ?? l.cantidadSolicitada ?? 0,
        cantidadRecibida: l.cantidadRecibidaHoy ?? l.cantidadRecibida ?? 0,
        cantidadRecibidaHoy: l.cantidadRecibidaHoy ?? l.cantidadRecibida ?? 0,
        cantidadRecibidaPrevia: l.cantidadRecibidaPrevia || 0,
        backorderPendiente: l.backorderPendiente ?? 0,
        cantidadRechazada: l.cantidadRechazada || 0,
        costoUnitarioUSD: l.costoUnitarioUSD || 0,
        costoAprobadoUSD: l.costoAprobadoUSD || l.costoUnitarioUSD || 0,
        toleranciaExcedente: l.toleranciaExcedente,
        notasDiscrepancia: l.notasDiscrepancia,
        actionOnRemaining: l.actionOnRemaining || 'BACKORDER',
        motivoSaldo: l.motivoSaldo,
        pedidoId: l.pedidoId,
        proyectoId: l.proyectoId,
        proyectoNombre: l.proyectoNombre,
        originalProyectoNombre: l.originalProyectoNombre,
        originalPedidoId: l.originalPedidoId,
        parentSolicitudId: l.parentSolicitudId,
        originalCantidadAprobada: l.originalCantidadAprobada,
        isSplitChild: l.isSplitChild,
        bultos: l.bultos,
        cantEnBulto: l.cantEnBulto,
        desgloseBultos: l.desgloseBultos,
        rutaLogistica: l.rutaLogistica,
        orderLineId: l.orderLineId,
        isDirectOrService: l.isDirectOrService
      }))
    };

    // Caso 1: Dispositivo offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      try {
        await queueOfflineReception(payload);
        setReceptionComplete({
          status: 'queued_offline',
          nriFolio: provisionalOfflineNri,
          offlineNriFolio: provisionalOfflineNri,
          actaRechazoFolio: hasRejectedUnits ? `RECHAZO-${provisionalOfflineNri}` : null,
          folioOAB: activeFolio,
          estadoGeneral: 'En cola offline (Rampa)',
          itemsProcesados: itemsToReceive.length,
          message: 'Sin conexión a Internet. Recepción guardada en IndexedDB local con folio provisional; se sincronizará con Notion ERP en cuanto se restablezca la señal.'
        });
        if (onReceptionSuccess) onReceptionSuccess(itemsToReceive);
      } catch (idbErr: any) {
        setErrorMessage(`Error guardando en cola offline: ${idbErr.message}`);
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // Caso 2: Intento online con Saga Multi-Lote y protección anti-falso-offline (PLAN-055)
    try {
      setBatchProgress(null);
      const result = await registerReception(payload, (prog) => setBatchProgress(prog));
      setReceptionComplete(result);
      if (onReceptionSuccess) onReceptionSuccess(itemsToReceive);
    } catch (netErr: any) {
      const isTrueNetworkDrop =
        (typeof navigator !== 'undefined' && !navigator.onLine) ||
        netErr?.isNetworkError === true;

      if (isTrueNetworkDrop) {
        console.warn('Caída real de red en rampa. Guardando en cola offline (IndexedDB):', netErr);
        try {
          await queueOfflineReception(payload);
          setReceptionComplete({
            status: 'queued_offline',
            nriFolio: provisionalOfflineNri,
            offlineNriFolio: provisionalOfflineNri,
            actaRechazoFolio: hasRejectedUnits ? `RECHAZO-${provisionalOfflineNri}` : null,
            folioOAB: activeFolio,
            estadoGeneral: 'En cola offline (Rampa)',
            itemsProcesados: itemsToReceive.length,
            message: 'Se detectó caída real de red. La recepción se guardó de forma segura en la memoria de la terminal (IndexedDB) para su sincronización diferida.'
          });
          if (onReceptionSuccess) onReceptionSuccess(itemsToReceive);
        } catch (fallbackErr: any) {
          setErrorMessage(`Fallo de red y de persistencia local: ${fallbackErr.message}`);
        }
      } else {
        // Error HTTP explícito del servidor (400/401/403/409/422/500): NO contaminar IndexedDB, permitir reintento auto-curativo
        console.error('Error HTTP en recepción de rampa (no se encola offline):', netErr);
        const statusLabel = netErr?.status ? ` (HTTP ${netErr.status})` : '';
        setErrorMessage(
          `No se completó el asiento en servidor${statusLabel}: ${netErr?.message || 'Error inesperado'}. Ningún dato se envió a la cola offline; puedes pulsar Confirmar nuevamente para reintentar con auto-curación.`
        );
      }
    } finally {
      setIsProcessing(false);
      setBatchProgress(null);
    }
  };

  // PLAN-064 (P64-D1): Agrupación consolidada por solicitud raíz para evaluar saldos y sobredespachos en familias 1:N
  const getConsolidatedSolicitudSummary = () => {
    const groups = new Map<string, {
      rootKey: string;
      approved: number;
      prevRec: number;
      recHoySum: number;
      pending: number;
    }>();

    itemsToReceive.forEach((it, idx) => {
      const rootKey = it.parentSolicitudId || (it.id ? it.id.split(/_(?:split|child)_/)[0] : '') || `_idx_${idx}`;
      const recHoy = it.cantidadRecibidaHoy !== undefined ? it.cantidadRecibidaHoy : (it.cantidadRecibida || 0);
      const itemApp = it.cantidadAprobada ?? it.cantidadSolicitada ?? 0;

      const existing = groups.get(rootKey);
      if (!existing) {
        const origApp = it.originalCantidadAprobada ?? itemApp;
        const prev = it.cantidadRecibidaPrevia || 0;
        groups.set(rootKey, {
          rootKey,
          approved: origApp,
          prevRec: prev,
          recHoySum: recHoy,
          pending: Math.max(0, origApp - prev)
        });
      } else {
        existing.recHoySum += recHoy;
        if (it.originalCantidadAprobada && it.originalCantidadAprobada > existing.approved) {
          existing.approved = it.originalCantidadAprobada;
          existing.pending = Math.max(0, existing.approved - existing.prevRec);
        }
      }
    });

    return Array.from(groups.values());
  };

  // Helper compartido para evaluar si se requiere autorización de Supervisor (PLAN-055 + PLAN-064)
  const checkHasExcessOverTolerance = () => {
    return getConsolidatedSolicitudSummary().some(g => g.pending > 0 && g.recHoySum > g.pending * 1.02);
  };

  // Compuerta previa de validación y gobernanza de rampa (PLAN-040 / PLAN-055 / PLAN-064: Saldos PRIMERO -> Supervisor SEGUNDO -> Ejecución)
  const handlePreConfirmReception = () => {
    setErrorMessage(null);

    if (!notaEntrega.trim()) {
      setValidationError('Por favor indica el Número de Nota de Entrega física o pulsa el botón [Sin Guía (S/N)].');
      return;
    }

    if (itemsToReceive.length === 0) {
      setValidationError('Debe haber al menos un material activo para asentar la recepción.');
      return;
    }

    // Regla de Foto y Válvula de Bypass (PLAN-040)
    if (!photoPreview && !motivoBypassFoto.trim() && tipoDocumento !== 'SN') {
      setShowBypassPrompt(true);
      setValidationError('Se requiere capturar la foto física del documento o registrar el motivo de Bypass.');
      return;
    }

    setValidationError(null);

    // COMPUERTA 1 (Negocio Operativo): Asistente Tripartito de Saldos PRIMERO (PLAN-055 + PLAN-064 consolidado por solicitud raíz)
    const incompleteGroups = getConsolidatedSolicitudSummary().filter(g => g.pending > 0 && g.recHoySum < g.pending);

    if (incompleteGroups.length > 0 && !balanceWizardConfirmed) {
      setShowBalanceWizardModal(true);
      return;
    }

    // COMPUERTA 2 (Seguridad / Step-Up Auth): Sobredespacho > 2% o Rectificaciones MTO SEGUNDO / FINAL (PLAN-055)
    const hasExcessOverTolerance = checkHasExcessOverTolerance();
    if (hasExcessOverTolerance && !verifiedSupervisorData) {
      setValidationError('Se detectó sobredespacho superior al 2% de tolerancia técnica. Requiere autorización de Supervisor.');
      setShowSupervisorAuthModal(true);
      return;
    }

    if (hasRectifications && !verifiedSupervisorData) {
      setShowSupervisorAuthModal(true);
      return;
    }

    executeConfirmReception();
  };

  // Callback al autenticar exitosamente supervisor (PLAN-012 / PLAN-043)
  const handleSupervisorAuthorized = async (supData: SupervisorAuthData) => {
    setVerifiedSupervisorData(supData);
    setShowSupervisorAuthModal(false);
    await executeConfirmReception(supData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 sm:p-4 no-print">
      <div className="bg-surface border border-borderSubtle rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl">
        
        {/* Terminal Header */}
        <div className="px-5 py-3 bg-surfaceHigh border-b border-borderSubtle flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-signal-blue/20 text-signal-blue border border-signal-blue/40">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-100 tracking-tight flex items-center gap-2">
                <span>Terminal Táctil de Recepción en Rampa</span>
                <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  ALMACÉN CENTRAL
                </span>
                {!isOnline && (
                  <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-1">
                    <WifiOff className="w-3 h-3" />
                    Modo Offline
                  </span>
                )}
                {pendingPhotosCount > 0 && (
                  <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 flex items-center gap-1" title="Fotos encoladas para reintento automático hacia Cloudflare R2">
                    <ImageIcon className="w-3 h-3" />
                    {pendingPhotosCount} foto(s) pend. R2
                  </span>
                )}
              </h2>
              <p className="text-[11px] text-slate-400">
                Conteo físico, Nota de Recepción Interna (NRI), Acta de Rechazo e Historial en BD_Recepciones_Rampa
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Conmutador de Pestaña: Nueva Recepción vs Historial NRI (PLAN-046 / Nodo 4/4) */}
            <div className="flex items-center gap-1 bg-page p-1 rounded-lg border border-borderSubtle">
              <button
                type="button"
                onClick={() => {
                  setHistoryPrintTarget(null);
                  setActiveTerminalTab('RECEIVE');
                }}
                className={`px-3 py-1 rounded-md text-xs font-bold transition flex items-center gap-1.5 ${
                  activeTerminalTab === 'RECEIVE'
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Truck className="w-3.5 h-3.5" />
                <span>🚚 Nueva Recepción</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTerminalTab('HISTORY')}
                className={`px-3 py-1 rounded-md text-xs font-bold transition flex items-center gap-1.5 ${
                  activeTerminalTab === 'HISTORY'
                    ? 'bg-brand-500/20 text-brand-300 border border-brand-500/40'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <FileText className="w-3.5 h-3.5" />
                <span>📜 Historial de Recepciones (NRI)</span>
              </button>
            </div>

            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white p-1 rounded-md hover:bg-surfaceHighest transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {activeTerminalTab === 'HISTORY' ? (
          <RampReceptionHistoryView
            initialNriFolio={initialNriFolio || (initialSearchTerm && (initialSearchTerm.startsWith('NRI-') || initialSearchTerm.startsWith('RECHAZO-')) ? initialSearchTerm : null)}
            initialFolioOAB={initialSearchTerm && initialSearchTerm.startsWith('OAB-') ? initialSearchTerm : undefined}
            onReprintEntry={(nriDetail) => {
              setHistoryPrintTarget(nriDetail);
              setShowPrintEntryModal(true);
            }}
            onReprintRejection={(nriDetail) => {
              setHistoryPrintTarget(nriDetail);
              setShowPrintRejectionModal(true);
            }}
            onReprintLabels={(nriDetail) => {
              if (Array.isArray(nriDetail.labelsSnapshot) && nriDetail.labelsSnapshot.length > 0) {
                setLabelsToPrint(nriDetail.labelsSnapshot);
              } else {
                const reconstructed = buildLabelsFromCurrentItems(
                  (nriDetail.itemsRecibidos || []).map((it: any) => ({
                    id: it.solicitudId || it.insumoId || it.nombre,
                    insumoId: it.insumoId,
                    nombre: it.nombre,
                    codigo: it.codigo,
                    unidadMedida: it.unidadMedida || 'und',
                    cantidadSolicitada: it.cantidadAprobada,
                    cantidadAprobada: it.cantidadAprobada,
                    cantidadRecibida: it.cantidadRecibidaHoy,
                    cantidadRecibidaHoy: it.cantidadRecibidaHoy,
                    cantidadRechazada: 0,
                    costoUnitarioUSD: 0,
                    bultos: it.bultos || 1,
                    cantEnBulto: it.cantEnBulto || it.cantidadRecibidaHoy || 1,
                    desgloseBultos: it.desgloseBultos,
                    proyectoNombre: it.proyectoNombre,
                    rotularEtiqueta: true
                  }))
                );
                setLabelsToPrint(reconstructed);
              }
              setShowLabelModal(true);
            }}
          />
        ) : receptionComplete ? (
          // Vista de Éxito / Comprobante de Entrada (Desacoplada en PLAN-048)
          <ReceptionSuccessNriPanel
            receptionComplete={receptionComplete}
            selectedFolio={selectedFolio}
            activeSubOrderFolio={activeSubOrderFolio}
            subOrdenesList={subOrdenesList}
            pendingOrders={pendingOrders}
            itemsToReceive={itemsToReceive}
            labelsToPrint={labelsToPrint}
            notaEntrega={notaEntrega}
            choferNombre={choferNombre}
            placaVehiculo={placaVehiculo}
            storesSummary={
              selectedOabStoresSummary ||
              computeOABStoresSummary(itemsToReceive) ||
              itemsToReceive.find(it => it.proyectoNombre)?.proyectoNombre
            }
            nriPrinted={nriPrinted}
            rejectionPrinted={rejectionPrinted}
            onPrintNriEntry={() => {
              setNriPrinted(true);
              setHistoryPrintTarget(null);
              setShowPrintEntryModal(true);
            }}
            onPrintRejectionAct={() => {
              setRejectionPrinted(true);
              setHistoryPrintTarget(null);
              setShowPrintRejectionModal(true);
            }}
            onOpenHistoryTab={() => {
              setActiveTerminalTab('HISTORY');
            }}
            onOpenLabelModal={() => setShowLabelModal(true)}
            onSelectSubOrderFromSuccess={(folio, numeroCotizacion) => {
              setReceptionComplete(null);
              setNriPrinted(false);
              setRejectionPrinted(false);
              handleSelectSubOrderTab(folio);
              setPhotoPreview(null);
              setNotaEntrega(numeroCotizacion || '');
            }}
            onTriggerRequote={(targetRequote, folio) => {
              if (onRequestRequote) {
                onRequestRequote(targetRequote, folio);
              } else {
                onClose();
              }
            }}
            onClose={onClose}
          />
        ) : (
          // Formulario de Recepción en Rampa
          <>
            {/* Meta bar: Selector de OAB, N° Nota de Entrega, Fecha, Tasa BCV, Foto */}
            <div className="p-3 bg-page border-b border-borderSubtle grid grid-cols-1 sm:grid-cols-5 gap-3 text-xs">
              <div>
                <label className="block text-[10px] uppercase text-slate-400 font-semibold mb-1">
                  Seleccionar OAB en Camino
                </label>
                <select
                  value={selectedFolio}
                  onChange={(e) => handleSelectOAB(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs font-mono font-bold bg-surface border border-borderSubtle rounded text-brand-400"
                >
                  <option value="">-- Ingreso Directo en Rampa --</option>
                  {pendingOrders.map(o => {
                    const isBifurcated = o.hasSubOrdenes || (o.subOrdenesCount && o.subOrdenesCount > 0);
                    const isChild = o.isSubOrden;
                    return (
                      <option key={o.id} value={o.folio}>
                        {isBifurcated ? '🔀 ' : isChild ? '  ↳ 🚚 ' : ''}
                        {o.folio} ({o.estadoGeneral}) · {isBifurcated ? `[Bifurcada: ${o.subOrdenesCount} proveedores]` : (o.proveedor || 'Sin Prov.')}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] uppercase text-slate-400 font-semibold">
                    Documento de Respaldo *
                  </label>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setTipoDocumento('NOTA_ENTREGA');
                        if (notaEntrega === 'S/N') setNotaEntrega('');
                      }}
                      className={`text-[9px] font-mono px-1 rounded transition ${
                        tipoDocumento === 'NOTA_ENTREGA' ? 'bg-cyan-500/20 text-cyan-300 font-bold border border-cyan-500/40' : 'text-slate-400 hover:text-slate-200'
                      }`}
                      title="Nota de Entrega comercial"
                    >
                      NE
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTipoDocumento('SENIAT');
                        setShowFiscalInputs(true);
                        if (notaEntrega === 'S/N') setNotaEntrega('');
                      }}
                      className={`text-[9px] font-mono px-1 rounded transition ${
                        tipoDocumento === 'SENIAT' ? 'bg-emerald-500/20 text-emerald-300 font-bold border border-emerald-500/40' : 'text-slate-400 hover:text-slate-200'
                      }`}
                      title="Factura Legal SENIAT"
                    >
                      SENIAT
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTipoDocumento('FLETE_INTERNO');
                        if (notaEntrega === 'S/N') setNotaEntrega('');
                      }}
                      className={`text-[9px] font-mono px-1 rounded transition ${
                        tipoDocumento === 'FLETE_INTERNO' ? 'bg-purple-500/20 text-purple-300 font-bold border border-purple-500/40' : 'text-slate-400 hover:text-slate-200'
                      }`}
                      title="Guía de Traslado o Flete Interno"
                    >
                      Flete
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTipoDocumento('SN');
                        setNotaEntrega('S/N');
                        setValidationError(null);
                      }}
                      className={`text-[9px] font-mono px-1 rounded transition ${
                        tipoDocumento === 'SN' ? 'bg-amber-500/20 text-amber-300 font-bold border border-amber-500/40' : 'text-slate-400 hover:text-slate-200'
                      }`}
                      title="Sin documento impreso (S/N)"
                    >
                      S/N
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder={
                    tipoDocumento === 'SENIAT' ? 'N° Factura (Ej: FAC-8201)' :
                    tipoDocumento === 'FLETE_INTERNO' ? 'N° Guía Flete (Ej: GUIA-102)' :
                    tipoDocumento === 'SN' ? 'S/N' : 'N° Nota (Ej: NE-9941)'
                  }
                  value={notaEntrega}
                  onChange={(e) => {
                    setNotaEntrega(e.target.value);
                    if (validationError) setValidationError(null);
                  }}
                  className={`w-full px-2.5 py-1.5 text-xs font-mono font-bold bg-surface border rounded text-slate-100 placeholder-slate-500 transition ${
                    validationError && !notaEntrega.trim() ? 'border-red-500 ring-1 ring-red-500' : 'border-borderSubtle'
                  }`}
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] uppercase text-slate-400 font-semibold">
                    Fecha de Descarga
                  </label>
                  <span className="text-[9px] font-mono text-slate-500 uppercase">Hoy</span>
                </div>
                <input
                  type="date"
                  value={fechaRecepcion}
                  onChange={(e) => setFechaRecepcion(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs font-mono bg-surface border border-borderSubtle rounded text-slate-200 mb-1"
                />
                {/* Selector de Cuadrilla Destino MTO (PLAN-040) */}
                <select
                  value={targetCuadrilla}
                  onChange={(e) => setTargetCuadrilla(e.target.value)}
                  className="w-full px-2 py-1 text-[10px] font-mono bg-surfaceHigh border border-borderSubtle rounded text-slate-300"
                  title="Cuadrilla de taller destino para rotulado y alerta de Telegram MTO"
                >
                  <option value="ALMACEN">📦 Almacén Central (General)</option>
                  <option value="HERRERIA">🔨 Herrería (Taller 1)</option>
                  <option value="CARPINTERIA">🪚 Carpintería (Taller 2)</option>
                  <option value="PINTURA">🎨 Pintura (Cabina)</option>
                  <option value="EMBALAJE">📦 Embalaje (Despacho)</option>
                  <option value="INSTALACION">🔧 Instalación (Obra)</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] uppercase text-slate-400 font-semibold">
                    Tasa BCV (Bs/USD)
                  </label>
                  {liveBcvRate && Math.abs(liveBcvRate - tasaBCV) > 0.05 && (
                    <button
                      type="button"
                      onClick={() => setTasaBCV(liveBcvRate)}
                      className="px-1.5 py-0.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 rounded text-[9px] font-bold font-mono transition flex items-center gap-1 active:scale-95"
                      title={`Actualizar a la tasa oficial de hoy (Bs ${liveBcvRate.toFixed(2)})`}
                    >
                      <span>⚡ Hoy: Bs {liveBcvRate.toFixed(2)}</span>
                    </button>
                  )}
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={tasaBCV}
                  onChange={(e) => setTasaBCV(Number(e.target.value) || 1.0)}
                  className="w-full px-2.5 py-1.5 text-xs font-mono font-bold bg-surface border border-borderSubtle rounded text-emerald-400"
                />
                <div className="flex items-center justify-between text-[9px] font-mono mt-1">
                  {liveBcvRate && Math.abs(liveBcvRate - tasaBCV) > 0.05 ? (
                    <span className="text-amber-400 font-bold">
                      Δ {liveBcvRate > tasaBCV ? '+' : ''}{(((liveBcvRate - tasaBCV) / tasaBCV) * 100).toFixed(1)}% vs hoy
                    </span>
                  ) : (
                    <span className="text-emerald-400">✓ Tasa al día</span>
                  )}
                  <span className="text-slate-500">Oficial BCV</span>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] uppercase text-slate-400 font-semibold">
                    Foto Documento
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowBypassPrompt(prev => !prev)}
                    className="text-[9px] font-mono text-amber-400 hover:text-amber-300 underline"
                    title="Válvula de excepción si no se dispone de foto física en rampa"
                  >
                    {showBypassPrompt ? 'Ocultar Bypass' : 'Bypass Foto'}
                  </button>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded border text-xs font-medium transition ${
                      photoPreview
                        ? 'bg-emerald-950/40 border-emerald-500/50 text-emerald-400'
                        : 'bg-surfaceHigh hover:bg-surfaceHighest border-borderSubtle text-slate-300'
                    }`}
                  >
                    <Camera className="w-3.5 h-3.5" />
                    <span>{photoPreview ? 'Foto Lista ✓' : 'Tomar Foto'}</span>
                  </button>

                  {photoPreview && (
                    <button
                      type="button"
                      onClick={() => setPhotoPreview(null)}
                      title="Eliminar foto"
                      className="p-1.5 bg-red-900/30 hover:bg-red-800/40 text-red-400 rounded border border-red-500/40"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Válvula de Bypass de Foto (PLAN-040) */}
                {showBypassPrompt && (
                  <div className="mt-1">
                    <input
                      type="text"
                      placeholder="Motivo bypass: ej. Chofer apurado / Lluvia"
                      value={motivoBypassFoto}
                      onChange={(e) => setMotivoBypassFoto(e.target.value)}
                      className="w-full px-1.5 py-0.5 text-[10px] font-mono bg-surface border border-amber-500/40 rounded text-amber-300 placeholder-slate-600 focus:outline-none"
                    />
                  </div>
                )}

                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoCapture}
                  className="hidden"
                />
              </div>
            </div>

            {/* Sección Opcional: Transporte / Chofer (PLAN-048) y Facturación Legal SENIAT (Fase 9C) */}
            <div className="px-4 py-2 bg-surfaceHigh/60 border-b border-borderSubtle flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-[10px] uppercase text-slate-400 font-mono">Chofer:</span>
                  <input
                    type="text"
                    placeholder="Nombre del transportista"
                    value={choferNombre}
                    onChange={(e) => setChoferNombre(e.target.value)}
                    className="w-36 sm:w-44 px-2 py-1 text-xs font-mono bg-surface border border-borderSubtle rounded text-slate-200 placeholder-slate-600 focus:border-emerald-400"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase text-slate-400 font-mono">Placa:</span>
                  <input
                    type="text"
                    placeholder="Ej: A12BC3D"
                    value={placaVehiculo}
                    onChange={(e) => setPlacaVehiculo(e.target.value.toUpperCase())}
                    className="w-24 px-2 py-1 text-xs font-mono uppercase bg-surface border border-borderSubtle rounded text-slate-200 placeholder-slate-600 focus:border-emerald-400"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowFiscalInputs(prev => !prev)}
                  className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-300 hover:text-white transition"
                >
                  <FileText className="w-3.5 h-3.5 text-brand-400" />
                  <span>🏛️ Factura Fiscal SENIAT (Opcional)</span>
                  <span className="text-[10px] text-slate-400 underline ml-1">
                    {showFiscalInputs ? 'Ocultar' : (numeroFacturaFiscal ? `FAC: ${numeroFacturaFiscal}` : 'Desplegar')}
                  </span>
                </button>

                {showFiscalInputs && (
                  <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto mt-1 sm:mt-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] uppercase text-slate-400 font-mono">N° Factura:</span>
                      <input
                        type="text"
                        placeholder="Ej: 001248"
                        value={numeroFacturaFiscal}
                        onChange={(e) => setNumeroFacturaFiscal(e.target.value)}
                        className="w-28 px-2 py-1 text-xs font-mono bg-surface border border-borderSubtle rounded text-slate-200 placeholder-slate-600 focus:border-brand-400"
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] uppercase text-slate-400 font-mono">N° Control:</span>
                      <input
                        type="text"
                        placeholder="Ej: 00-019842"
                        value={numeroControlFiscal}
                        onChange={(e) => setNumeroControlFiscal(e.target.value)}
                        className="w-28 px-2 py-1 text-xs font-mono bg-surface border border-borderSubtle rounded text-slate-200 placeholder-slate-600 focus:border-brand-400"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Banner de Búsqueda o Errores */}
            {isSearching && (
              <div className="py-2 px-4 bg-brand-500/10 border-b border-brand-500/20 text-center text-xs text-brand-400 font-mono animate-pulse">
                Cargando renglones aprobados de la OAB desde Notion...
              </div>
            )}

            {errorMessage && (
              <div className="m-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400 flex items-center gap-2">
                <AlertOctagon className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            {validationError && (
              <div className="m-3 p-3 bg-red-500/15 border border-red-500/40 rounded-lg text-xs text-red-300 flex items-center justify-between animate-pulse">
                <div className="flex items-center gap-2">
                  <AlertOctagon className="w-4 h-4 text-red-400 shrink-0" />
                  <span>{validationError}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setValidationError(null)}
                  className="p-1 text-red-400 hover:text-white rounded"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Buscador de Insumos para Ingreso Directo en Rampa */}
            <div className="px-4 py-2.5 bg-surfaceHigh/40 border-b border-borderSubtle">
              <div className="relative">
                <div className="flex items-center gap-2 bg-surface border border-borderSubtle focus-within:border-brand-500 rounded-lg px-3 py-2 text-xs transition">
                  <Search className="w-4 h-4 text-slate-400 shrink-0" />
                  <input
                    type="text"
                    value={searchItemQuery}
                    onChange={(e) => setSearchItemQuery(e.target.value)}
                    placeholder="Buscar insumo por código o nombre para agregar a rampa..."
                    className="w-full bg-transparent text-slate-100 placeholder-slate-500 focus:outline-none font-sans"
                  />
                  {searchItemQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchItemQuery('')}
                      className="text-slate-400 hover:text-white p-0.5"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Dropdown flotante de resultados */}
                {filteredInventory.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-surfaceHigh border border-borderSubtle rounded-lg shadow-2xl z-30 max-h-60 overflow-y-auto">
                    {filteredInventory.map(item => (
                      <div
                        key={item.id}
                        onClick={() => handleAddItem(item)}
                        className="px-3 py-2 hover:bg-surfaceHighest cursor-pointer flex items-center justify-between border-b border-borderSubtle/50 last:border-0 text-xs transition"
                      >
                        <div>
                          <div className="font-semibold text-slate-100 flex items-center gap-2">
                            <span>{item.nombre}</span>
                            {item.codigo && (
                              <span className="font-mono text-[10px] text-brand-400 bg-brand-500/10 px-1.5 py-0.5 rounded border border-brand-500/20">
                                {item.codigo}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-400">
                            {item.categoriaMaterial || 'Insumo'} · Stock actual: {item.stockBase} {item.unidad || 'und'}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-right">
                          <div className="text-[10px] font-mono text-emerald-400">
                            ${(item.costoUnitarioUSD || 0).toFixed(2)} USD
                          </div>
                          <button
                            type="button"
                            className="p-1 rounded bg-brand-500/20 text-brand-400 hover:bg-brand-500/30 transition"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Pestañas de Sub-Órdenes por Proveedor (PLAN-039 / D4) */}
            {subOrdenesList.length > 0 && (
              <div className="px-4 py-2 bg-page border-b border-borderSubtle flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5 mr-2">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-brand-400 flex items-center gap-1">
                    <Truck className="w-3.5 h-3.5" />
                    <span>Sub-Órdenes / Camiones:</span>
                  </span>
                  <span className="text-[9px] px-1.5 py-0.2 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30 font-mono">
                    Bifurcada ({subOrdenesList.length})
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 flex-1">
                  {subOrdenesList.map((sub, sIdx) => {
                    const isSelected = activeSubOrderFolio === sub.folio;
                    const isCompleted = sub.estadoGeneral === 'Completada' || sub.estadoGeneral === 'Recibido Completo';
                    const isPartial = sub.estadoGeneral === 'Recepción Parcial';

                    return (
                      <button
                        key={sub.id || sIdx}
                        type="button"
                        onClick={() => handleSelectSubOrderTab(sub.folio)}
                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition border ${
                          isSelected
                            ? 'bg-brand-500/20 text-brand-300 border-brand-500 shadow-sm'
                            : 'bg-surfaceHigh hover:bg-surfaceHighest text-slate-300 border-borderSubtle'
                        }`}
                      >
                        <span className="font-mono text-[11px] font-bold">
                          {sub.folio}
                        </span>
                        <span className="text-slate-400">|</span>
                        <span className="truncate max-w-[130px] text-slate-200">
                          {sub.proveedor || `Proveedor ${sub.letter || sIdx + 1}`}
                        </span>
                        <span className={`text-[9px] font-mono px-1.5 py-0.2 rounded border ${
                          isCompleted
                            ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                            : isPartial
                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                            : 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                        }`}>
                          {sub.estadoGeneral || 'En Compra'}
                        </span>
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => handleSelectSubOrderTab(null)}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition border ${
                      activeSubOrderFolio === null
                        ? 'bg-slate-700 text-white border-slate-500'
                        : 'bg-surface text-slate-400 hover:text-slate-200 border-borderSubtle'
                    }`}
                  >
                    <span>📋 Ver Todas ({itemsToReceive.length})</span>
                  </button>
                </div>
              </div>
            )}

            {/* Listado de Materiales a Recibir */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400 mb-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span>Materiales a verificar ({activeSubOrder ? `${visibleItems.length} de ${itemsToReceive.length}` : itemsToReceive.length}):</span>
                  {selectedFolio && visibleItems.length > 0 && (() => {
                    const enteringTodayCount = visibleItems.filter(it => (it.cantidadRecibidaHoy ?? it.cantidadRecibida ?? 0) > 0).length;
                    const nextFreightZeroCount = visibleItems.length - enteringTodayCount;
                    return (
                      <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px]">
                        <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-semibold">
                          🟢 Ingresando hoy: {enteringTodayCount} renglones
                        </span>
                        <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-semibold">
                          ⏳ Próximo flete (en 0): {nextFreightZeroCount} renglones
                        </span>
                      </div>
                    );
                  })()}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {selectedFolio && visibleItems.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={handleSetAllRemainingAsNextFreight}
                        className="px-2.5 py-1 text-[11px] font-semibold bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 rounded transition active:scale-95"
                        title="Inicia todos los ítems en 0 recibidos hoy para que solo marques los que bajaron en este flete parcial"
                      >
                        ⏳ Iniciar Todo en 0 (Recepción Parcial)
                      </button>
                      <button
                        type="button"
                        onClick={handleSetAllFull}
                        className="px-2.5 py-1 text-[11px] font-semibold bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 rounded transition active:scale-95"
                        title="Marca el 100% del saldo pendiente como recibido hoy en todos los renglones visibles"
                      >
                        ✓ Marcar Todo al 100%
                      </button>
                    </>
                  )}
                </div>
              </div>

              {visibleItems.length === 0 ? (
                <div className="text-center py-12 px-4 border border-dashed border-borderSubtle rounded-xl bg-page/40 my-4">
                  <div className="w-12 h-12 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center mx-auto mb-3 text-slate-400">
                    <Search className="w-6 h-6" />
                  </div>
                  <h4 className="text-sm font-semibold text-slate-200">
                    {activeSubOrder
                      ? `Sub-Orden ${activeSubOrder.folio} sin renglones pendientes`
                      : selectedFolio
                      ? 'Orden sin renglones pendientes'
                      : 'Rampa limpia: Sin materiales cargados'}
                  </h4>
                  <p className="text-xs text-slate-400 max-w-md mx-auto mt-1">
                    {activeSubOrder
                      ? `Todos los insumos de ${activeSubOrder.proveedor || 'este proveedor'} han sido recibidos conforme.`
                      : selectedFolio
                      ? 'Esta orden no tiene renglones pendientes por recibir en este momento.'
                      : 'Usa el buscador superior para agregar los insumos físicos que están ingresando directamente a la planta sin OAB.'}
                  </p>
                </div>
              ) : (
                visibleItems.map((item) => {
                  const idx = itemsToReceive.indexOf(item);
                  const isFromOAB = Boolean(item.id || (item.cantidadAprobada && item.cantidadAprobada > 0));
                  const approvedQty = isFromOAB ? (item.cantidadAprobada || 0) : 0;
                  const prevRecQty = item.cantidadRecibidaPrevia || 0;
                  const pendingBalance = Math.max(0, approvedQty - prevRecQty);
                  const hasDiscrepancy = (item.cantidadRechazada || 0) > 0 || (item.backorderPendiente || 0) > 0;

                  return (
                    <div
                      key={item.id || idx}
                      className={`p-3 rounded-lg border transition-all ${
                        hasDiscrepancy
                          ? 'bg-amber-950/20 border-amber-500/40'
                          : 'bg-page/70 border-borderSubtle hover:border-slate-600'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-sm text-slate-100">{item.nombre}</span>
                            {item.proveedorAdjudicado && (
                              <span className="text-[10px] bg-purple-500/10 text-purple-300 border border-purple-500/20 px-1.5 py-0.5 rounded font-mono">
                                🚚 {item.proveedorAdjudicado}
                              </span>
                            )}
                            {(item.isDirectOrService || item.rutaLogistica === 'DIRECTO_OBRA' || item.rutaLogistica === 'SERVICIO_SITIO') && (
                              <span className="text-[10px] bg-violet-500/20 text-violet-300 border border-violet-500/40 px-1.5 py-0.5 rounded font-mono font-bold">
                                🏗️ Conformidad en Obra / Sitio (Sin Ingreso a Galpón)
                              </span>
                            )}
                          </div>
                          {isFromOAB ? (
                            <div className="flex flex-wrap items-center gap-1.5 mt-1 text-xs text-slate-400">
                              <span className="bg-surfaceHigh px-1.5 py-0.5 rounded border border-borderSubtle">
                                Aprobado: <strong className="text-slate-200 font-mono">{approvedQty}</strong>
                              </span>
                              <span className="bg-blue-950/40 px-1.5 py-0.5 rounded border border-blue-500/30 text-blue-300">
                                Recibido Previo: <strong className="font-mono">{prevRecQty}</strong>
                              </span>
                              <span className="bg-amber-950/40 px-1.5 py-0.5 rounded border border-amber-500/30 text-amber-300">
                                Saldo Pendiente: <strong className="font-mono">{pendingBalance}</strong>
                              </span>
                              <span className="text-slate-400 font-mono text-[11px] ml-1">
                                @ ${(item.costoAprobadoUSD || item.costoUnitarioUSD || 0).toFixed(2)} USD
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 ml-2">
                              Ingreso Directo · Stock actual en planta: <strong className="text-slate-200 font-mono">{item.cantidadStock ?? 0} und</strong>
                            </span>
                          )}
                          {item.proyectoNombre && (
                            <span className="text-[10px] text-brand-400 font-mono ml-2">
                              [{item.proyectoNombre}]
                            </span>
                          )}
                        </div>

                        {/* Botones táctiles de acción rápida y eliminación (papelera SOLO para ingreso manual) */}
                        <div className="flex items-center gap-1.5 self-end sm:self-auto">
                          {isFromOAB ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleSetFull(idx)}
                                className="px-2 py-1 text-[11px] font-semibold bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/40 rounded transition active:scale-95"
                                title="Marcar todo el saldo pendiente como recibido"
                              >
                                Todo ({pendingBalance})
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSetNextFreight(idx)}
                                className="px-2 py-1 text-[11px] font-semibold bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 rounded transition active:scale-95"
                                title="Marcar como Próximo Flete (0 recibidos hoy, se mantiene el saldo pendiente)"
                              >
                                ⏳ Próx. Flete
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(idx)}
                              title="Quitar este material de la rampa"
                              className="p-1 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleSetZero(idx)}
                            className="px-2 py-1 text-[11px] font-semibold bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/40 rounded transition active:scale-95"
                            title="Registrar como rechazo / 0 recibidos"
                          >
                            0
                          </button>
                        </div>
                      </div>

                    {/* Inputs de Conteo Físico y Valuación */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-[10px] text-emerald-400 uppercase font-semibold">
                            Conforme Hoy:
                          </label>
                          <button
                            type="button"
                            onClick={() => handleOpenUomModal(idx)}
                            className="px-1.5 py-0.5 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 border border-indigo-500/40 rounded text-[9px] font-bold font-mono transition flex items-center gap-0.5 active:scale-95"
                            title="Convertir desde kg o metros según remisión del proveedor (PLAN-042)"
                          >
                            <Scale className="w-2.5 h-2.5" />
                            <span>⚖️ Conv</span>
                          </button>
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={item.cantidadRecibidaHoy !== undefined ? item.cantidadRecibidaHoy : item.cantidadRecibida}
                          onChange={(e) => handleQuantityChange(idx, 'cantidadRecibida', Number(e.target.value))}
                          className="w-full px-2.5 py-1 text-center font-mono font-bold text-sm bg-surface border border-emerald-500/40 rounded text-emerald-400"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] text-red-400 uppercase font-semibold mb-1">
                          Rechazo Hoy:
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={item.cantidadRechazada}
                          onChange={(e) => handleQuantityChange(idx, 'cantidadRechazada', Number(e.target.value))}
                          className="w-full px-2.5 py-1 text-center font-mono font-bold text-sm bg-surface border border-red-500/40 rounded text-red-400"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] text-slate-300 uppercase font-semibold mb-1 flex items-center justify-between">
                          <span>Costo Unit. Real</span>
                          {item.costoAprobadoUSD && item.costoUnitarioUSD > item.costoAprobadoUSD * 1.05 && (
                            <span className="text-[9px] text-amber-400 font-bold font-mono">
                              +{(((item.costoUnitarioUSD - item.costoAprobadoUSD) / item.costoAprobadoUSD) * 100).toFixed(0)}%
                            </span>
                          )}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.costoUnitarioUSD}
                          onChange={(e) => handleQuantityChange(idx, 'costoUnitarioUSD', Number(e.target.value))}
                          className={`w-full px-2 py-1 text-center font-mono font-bold text-sm bg-surface border rounded ${
                            item.costoAprobadoUSD && item.costoUnitarioUSD > item.costoAprobadoUSD * 1.05
                              ? 'border-amber-500 text-amber-300'
                              : 'border-borderSubtle text-slate-100'
                          }`}
                        />
                        <div className="text-[10px] text-slate-500 font-mono text-center mt-0.5">
                          ≈ Bs {(item.costoUnitarioUSD * (tasaBCV || 1)).toFixed(2)}
                        </div>
                      </div>

                      <div className="col-span-2">
                        <label className="block text-[10px] text-slate-400 uppercase font-semibold mb-1 flex items-center justify-between">
                          <span>Motivo / Discrepancia</span>
                          <label className="flex items-center gap-1 cursor-pointer font-normal text-[10px] text-slate-400">
                            <input
                              type="checkbox"
                              checked={item.toleranciaExcedente || false}
                              onChange={() => handleToggleTolerance(idx)}
                              className="rounded border-borderSubtle text-brand-500 focus:ring-0"
                            />
                            <span>Tolerancia Perfil/Rollo</span>
                          </label>
                        </label>
                        <input
                          type="text"
                          placeholder="Ej: 2 unidades rotas, abolladura en caja..."
                          value={item.notasDiscrepancia || ''}
                          onChange={(e) => handleNotesChange(idx, e.target.value)}
                          className="w-full px-2 py-1 text-xs bg-surface border border-borderSubtle rounded text-slate-200 placeholder-slate-600"
                        />
                      </div>
                    </div>

                    {/* Barra Ergonómica de Rotulado de Proyecto / Bultos y Rectificación en Rampa (PLAN-012) */}
                    <div className="mt-2.5 pt-2 border-t border-borderSubtle/60 flex flex-wrap items-center justify-between gap-2 bg-surface/50 p-2 rounded">
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-slate-200">
                          <input
                            type="checkbox"
                            checked={item.rotularEtiqueta !== false}
                            onChange={() => handleToggleRotulado(idx)}
                            className="rounded border-borderSubtle text-brand-500 focus:ring-0"
                          />
                          <Tag className="w-3.5 h-3.5 text-brand-400" />
                          <span>Rotular Etiqueta</span>
                        </label>
                        {item.proyectoNombre && !item.proyectoNombre.toLowerCase().includes('stock general') && !item.proyectoNombre.toLowerCase().includes('stock fábrica') && !item.proyectoNombre.toLowerCase().includes('stock fabrica') ? (
                          <div className="flex items-center gap-1.5 bg-brand-500/20 text-brand-300 border border-brand-500/30 px-2 py-0.5 rounded font-mono text-[10px] font-bold">
                            <span>Obra: {item.proyectoNombre}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setActiveLineIdxForOrder(idx);
                                setIsOrderSearchOpen(true);
                              }}
                              className="px-1 py-0.5 text-slate-300 hover:text-white rounded hover:bg-brand-500/30 transition flex items-center gap-1"
                              title="Cambiar o reasignar destino en rampa"
                            >
                              <FolderKanban className="w-3 h-3 text-brand-300" />
                              <span className="text-[9px]">Cambiar</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setItemsToReceive(prev => {
                                  const copy = [...prev];
                                  copy[idx] = {
                                    ...copy[idx],
                                    proyectoNombre: undefined,
                                    pedidoId: undefined,
                                    proyectoId: undefined,
                                    rotularEtiqueta: true
                                  };
                                  return copy;
                                });
                              }}
                              className="p-0.5 text-red-400 hover:text-red-200 rounded hover:bg-red-500/20 transition"
                              title="Quitar obra y reclasificar a Stock General (MTS)"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 bg-slate-800 text-slate-300 border border-slate-700 px-2 py-0.5 rounded font-mono text-[10px]">
                            <Package className="w-3 h-3 text-emerald-400" />
                            <span className="font-semibold text-emerald-300">📦 Stock General (MTS)</span>
                            <button
                              type="button"
                              onClick={() => {
                                setActiveLineIdxForOrder(idx);
                                setIsOrderSearchOpen(true);
                              }}
                              className="px-1.5 py-0.5 text-[9px] text-brand-300 hover:text-white rounded border border-brand-500/30 bg-brand-500/10 hover:bg-brand-500/20 transition"
                              title="Asignar a una obra o pedido antes de recibir"
                            >
                              + Asignar Obra
                            </button>
                          </div>
                        )}

                        {/* Badges de Rectificación y Split (PLAN-012) */}
                        {isLineRectified(item) && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40"
                            title={`Destino modificado en rampa. Original: ${item.originalProyectoNombre || 'Stock General (MTS)'}`}
                          >
                            <AlertTriangle className="w-2.5 h-2.5 text-amber-400" />
                            <span>Rectificado</span>
                          </span>
                        )}
                        {item.isSplitChild && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/40">
                            <Scissors className="w-2.5 h-2.5 text-purple-400" />
                            <span>Fracción</span>
                          </span>
                        )}

                        {/* Botón Fraccionar / Split Destino (PLAN-012) */}
                        {(item.cantidadRecibidaHoy ?? item.cantidadRecibida ?? 1) > 1 && (
                          <button
                            type="button"
                            onClick={() => handleSplitDestination(idx)}
                            className="flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded transition active:scale-95"
                            title="Dividir cantidad entre dos obras o entre obra y Stock General"
                          >
                            <Scissors className="w-3 h-3 text-purple-400" />
                            <span>Dividir Destino</span>
                          </button>
                        )}
                      </div>

                      {(item.rotularEtiqueta !== false) && (() => {
                        const received = item.cantidadRecibidaHoy ?? item.cantidadRecibida ?? 1;
                        const std = Math.max(1, item.cantEnBulto || Math.ceil(received / (item.bultos || 1)));
                        const hasCustomDesglose = Boolean(item.desgloseBultos && item.desgloseBultos.length > 0);
                        const fullBultos = Math.floor(received / std);
                        const remainder = received % std;
                        const hasPico = !hasCustomDesglose && remainder > 0 && (item.bultos || 1) > 1;

                        return (
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-slate-400 uppercase font-semibold">Bultos:</span>
                              <input
                                type="number"
                                min="1"
                                max="99"
                                value={item.bultos || 1}
                                onChange={(e) => handleBultosChange(idx, Math.max(1, parseInt(e.target.value) || 1))}
                                className="w-12 px-1.5 py-0.5 text-center font-mono font-bold text-xs bg-surface border border-borderSubtle rounded text-slate-100"
                              />
                            </div>

                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-slate-400 uppercase font-semibold">Cant/Bulto:</span>
                              <input
                                type="number"
                                min="1"
                                value={item.cantEnBulto || Math.ceil((item.cantidadRecibidaHoy ?? item.cantidadRecibida ?? 1) / (item.bultos || 1))}
                                onChange={(e) => handleCantEnBultoChange(idx, Math.max(1, parseInt(e.target.value) || 1))}
                                className="w-16 px-1.5 py-0.5 text-center font-mono font-bold text-xs bg-surface border border-borderSubtle rounded text-emerald-400"
                              />
                              <span className="text-[10px] text-slate-500 font-mono">{item.unidadMedida || 'und'}</span>
                            </div>

                            {/* Badge Informativo de Bulto Pico o Desglose Asimétrico (D14) */}
                            {hasCustomDesglose ? (
                              <span className="px-2 py-0.5 text-[10px] font-mono font-bold bg-purple-950/40 text-purple-300 border border-purple-800/60 rounded">
                                📐 [{item.desgloseBultos!.join('+')}] {item.unidadMedida || 'und'}
                              </span>
                            ) : hasPico ? (
                              <span className="px-2 py-0.5 text-[10px] font-mono font-semibold bg-sky-950/40 text-sky-300 border border-sky-800/60 rounded" title="Cálculo automático de bulto pico">
                                📦 {fullBultos}×{std} + 1 pico de {remainder}
                              </span>
                            ) : null}

                            {/* Botón Drawer Desglose Asimétrico (D14) */}
                            <button
                              type="button"
                              onClick={() => handleOpenBreakdownDrawer(idx)}
                              className="px-2 py-0.5 text-[10px] font-bold bg-brand-500/10 hover:bg-brand-500/20 text-brand-300 border border-brand-500/30 rounded transition active:scale-95 flex items-center gap-1"
                              title="Configurar bultos asimétricos o irregulares enviados por el proveedor"
                            >
                              <span>📐</span>
                              <span>Desglose Irregular</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleResetLoteCompleto(idx)}
                              className="px-2 py-0.5 text-[10px] font-medium bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 rounded transition active:scale-95"
                              title="1 Etiqueta por el lote total recibido"
                            >
                              1 Lote Completo
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              }))}

              {/* Acordeón informativo de insumos recibidos al 100% en fletes previos */}
              {completedLines.length > 0 && (
                <div className="mt-4 border border-emerald-500/30 rounded-lg overflow-hidden bg-emerald-950/10">
                  <button
                    type="button"
                    onClick={() => setShowCompletedAccordion(prev => !prev)}
                    className="w-full px-4 py-2 flex items-center justify-between text-xs font-semibold text-emerald-400 hover:bg-emerald-950/20 transition"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      <span>📦 {completedLines.length} insumo(s) recibidos al 100% en fletes anteriores</span>
                    </div>
                    <span className="text-[11px] underline">
                      {showCompletedAccordion ? 'Ocultar' : 'Ver detalle'}
                    </span>
                  </button>
                  {showCompletedAccordion && (
                    <div className="p-3 border-t border-emerald-500/20 space-y-1.5 bg-surface/50">
                      {completedLines.map((cItem, cIdx) => (
                        <div key={cItem.id || cIdx} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-surface border border-borderSubtle">
                          <span className="text-slate-200 font-medium">{cItem.nombre}</span>
                          <span className="font-mono text-emerald-400 font-bold">
                            {cItem.cantidadRecibidaPrevia ?? cItem.cantidadAprobada} / {cItem.cantidadAprobada} und recibidas ✓
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Acordeón segregado de insumos NO aprobados / cancelados por Gerencia (PLAN-055) */}
              {canceledLines.length > 0 && (
                <div className="mt-3 border border-slate-700/80 rounded-lg overflow-hidden bg-slate-900/40">
                  <button
                    type="button"
                    onClick={() => setShowCanceledAccordion(prev => !prev)}
                    className="w-full px-4 py-2 flex items-center justify-between text-xs font-semibold text-slate-300 hover:bg-slate-800/50 transition"
                  >
                    <div className="flex items-center gap-2">
                      <AlertOctagon className="w-4 h-4 text-rose-400/80" />
                      <span>🚫 {canceledLines.length} insumo(s) no aprobados por Gerencia (excluidos de compra)</span>
                    </div>
                    <span className="text-[11px] underline text-slate-400">
                      {showCanceledAccordion ? 'Ocultar' : 'Ver detalle'}
                    </span>
                  </button>
                  {showCanceledAccordion && (
                    <div className="p-3 border-t border-slate-700/60 space-y-1.5 bg-surface/40">
                      {canceledLines.map((xItem, xIdx) => (
                        <div key={xItem.id || xIdx} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-surface/80 border border-borderSubtle/70">
                          <span className="text-slate-400 line-through">{xItem.nombre}</span>
                          <span className="font-mono text-rose-300/90 text-[11px]">
                            Solicitado: {xItem.cantidadSolicitada} · Aprobado: 0 und
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Terminal Footer */}
            <div className="p-3 bg-surfaceHigh border-t border-borderSubtle flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-4 font-mono">
                <div>
                  <span className="text-slate-400">Total a Asentar: </span>
                  <span className="text-emerald-400 font-bold">
                    {itemsToReceive.reduce((acc, i) => acc + (i.cantidadRecibida || 0), 0)} und
                  </span>
                  <span className="text-slate-400 font-mono ml-2">
                    (${itemsToReceive.reduce((acc, i) => acc + (i.cantidadRecibida || 0) * (i.costoUnitarioUSD || 0), 0).toFixed(2)} USD · Bs {(itemsToReceive.reduce((acc, i) => acc + (i.cantidadRecibida || 0) * (i.costoUnitarioUSD || 0), 0) * (tasaBCV || 1)).toFixed(2)})
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">Rechazos: </span>
                  <span className="text-red-400 font-bold">
                    {itemsToReceive.reduce((acc, i) => acc + (i.cantidadRechazada || 0), 0)} und
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {hasRectifications && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-300">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                    <span>
                      {verifiedSupervisorData
                        ? `✓ Autorizado por: ${verifiedSupervisorData.name}`
                        : `⚠️ ${rectifiedItems.length} destino(s) rectificado(s) — Requiere PIN`}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3.5 py-2 text-xs text-slate-400 hover:text-slate-200 transition rounded"
                >
                  Cancelar
                </button>
                {itemsToReceive.some(l => l.rotularEtiqueta !== false) && (
                  <button
                    type="button"
                    onClick={handlePreviewLabels}
                    className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-brand-300 bg-brand-500/10 hover:bg-brand-500/20 border border-brand-500/30 rounded-lg transition active:scale-95"
                    title="Previsualizar e imprimir etiquetas físicas universales antes de asentar"
                  >
                    <Tag className="w-3.5 h-3.5 text-brand-400" />
                    <span>Previsualizar Etiquetas</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={handlePreConfirmReception}
                  disabled={isProcessing || itemsToReceive.length === 0}
                  className="flex items-center gap-2 px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold rounded-lg text-xs transition shadow active:scale-95 disabled:opacity-50"
                >
                  {isProcessing ? (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>
                        {batchProgress && batchProgress.totalBatches > 1
                          ? `Procesando lote ${batchProgress.currentBatch}/${batchProgress.totalBatches}...`
                          : 'Procesando entrada...'}
                      </span>
                    </span>
                  ) : (
                    <>
                      {hasRectifications && !verifiedSupervisorData ? (
                        <ShieldAlert className="w-4 h-4 text-emerald-950" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      <span>
                        {hasRectifications && !verifiedSupervisorData
                          ? 'Autorizar Cambios y Asentar en Kardex'
                          : 'Confirmar Recepción y Asentar en Kardex'}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showLabelModal && (
        <PrintSheetProjectLabels
          items={labelsToPrint}
          onClose={() => setShowLabelModal(false)}
        />
      )}

      {/* Modal Táctil de Búsqueda y Asignación de Pedido / Obra MTO (Fase 11.3) */}
      {isOrderSearchOpen && (
        <OrderSearchModal
          isOpen={isOrderSearchOpen}
          onClose={() => {
            setIsOrderSearchOpen(false);
            setActiveLineIdxForOrder(null);
          }}
          onSelectOrder={(order: OrderReference) => {
            if (activeLineIdxForOrder !== null) {
              setItemsToReceive(prev => prev.map((it, idx) => {
                if (idx === activeLineIdxForOrder) {
                  const isStk = !order.id || order.codigo === 'STK' || (order.proyecto && order.proyecto.toLowerCase().includes('stock general'));
                  if (isStk) {
                    return {
                      ...it,
                      pedidoId: undefined,
                      proyectoId: undefined,
                      proyectoNombre: undefined,
                      rotularEtiqueta: true
                    };
                  }
                  return {
                    ...it,
                    pedidoId: order.id,
                    proyectoId: order.proyectoId || order.id,
                    proyectoNombre: `${order.codigo} - ${order.proyecto}`,
                    rotularEtiqueta: true
                  };
                }
                return it;
              }));
            }
            setIsOrderSearchOpen(false);
            setActiveLineIdxForOrder(null);
          }}
        />
      )}

      {/* Drawer Táctil de Desglose Asimétrico de Bultos (D14) */}
      {drawerOpen && activeDrawerItemIdx !== null && itemsToReceive[activeDrawerItemIdx] && (
        <BultoBreakdownDrawer
          isOpen={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setActiveDrawerItemIdx(null);
          }}
          materialNombre={itemsToReceive[activeDrawerItemIdx].nombre}
          sku={itemsToReceive[activeDrawerItemIdx].codigo}
          cantidadTotal={itemsToReceive[activeDrawerItemIdx].cantidadRecibidaHoy ?? itemsToReceive[activeDrawerItemIdx].cantidadRecibida ?? 1}
          unidadMedida={itemsToReceive[activeDrawerItemIdx].unidadMedida || 'und'}
          initialDesglose={itemsToReceive[activeDrawerItemIdx].desgloseBultos}
          onApplyDesglose={handleApplyDesglose}
        />
      )}

      {/* Modal de Autorización por PIN de Supervisor para Rectificación en Rampa (PLAN-012 / PLAN-043) */}
      <RampSupervisorAuthModal
        isOpen={showSupervisorAuthModal}
        onClose={() => setShowSupervisorAuthModal(false)}
        rectifiedItems={rectifiedItems}
        onAuthorized={handleSupervisorAuthorized}
      />

      {/* Modal Asistente Tripartito de Saldos en Rampa (PLAN-040 / PLAN-055: Encadena hacia Supervisor Auth si aplica) */}
      <RampBalanceWizardModal
        isOpen={showBalanceWizardModal}
        onClose={() => setShowBalanceWizardModal(false)}
        itemsToReceive={itemsToReceive}
        onApplyGlobalBalanceAction={handleApplyGlobalBalanceAction}
        onSetItemBalanceAction={handleSetItemBalanceAction}
        onConfirm={() => {
          setBalanceWizardConfirmed(true);
          setShowBalanceWizardModal(false);
          const hasExcess = checkHasExcessOverTolerance();
          if ((hasExcess || hasRectifications) && !verifiedSupervisorData) {
            if (hasExcess) {
              setValidationError('Se detectó sobredespacho superior al 2% de tolerancia técnica. Requiere autorización de Supervisor.');
            }
            setShowSupervisorAuthModal(true);
            return;
          }
          executeConfirmReception();
        }}
      />

      {/* Impresión Bimodal: Nota de Recepción Interna (NRI Carta) (PLAN-040 / PLAN-046) */}
      {showPrintEntryModal && (
        <PrintSheetRampEntry
          nriFolio={historyPrintTarget?.nriFolio || receptionComplete?.nriFolio}
          offlineNriFolio={historyPrintTarget?.offlineNriFolio || receptionComplete?.offlineNriFolio}
          actaRechazoFolio={historyPrintTarget?.actaRechazoFolio || receptionComplete?.actaRechazoFolio || null}
          folioOAB={historyPrintTarget?.folioOAB || receptionComplete?.folioOAB || selectedFolio || 'OAB-RAMPA'}
          proveedor={
            historyPrintTarget?.proveedor ||
            subOrdenesList.find(s => s.folio === activeSubOrderFolio)?.proveedor ||
            pendingOrders.find(o => o.folio === selectedFolio)?.proveedor
          }
          numeroNotaEntrega={historyPrintTarget?.numeroNotaEntrega || historyPrintTarget?.numeroGuia || notaEntrega}
          fechaRecepcion={historyPrintTarget?.fechaRecepcion || fechaRecepcion}
          tipoDocumento={historyPrintTarget?.tipoDocumento || tipoDocumento}
          receptorNombre={historyPrintTarget?.receptorNombre || historyPrintTarget?.receptorRampa || 'Operador de Rampa'}
          choferNombre={historyPrintTarget?.choferNombre || historyPrintTarget?.choferPlaca || choferNombre || undefined}
          placaVehiculo={historyPrintTarget?.placaVehiculo || placaVehiculo || undefined}
          tiendaDestino={
            historyPrintTarget?.tiendaDestino ||
            selectedOabStoresSummary ||
            computeOABStoresSummary(itemsToReceive) ||
            itemsToReceive.find(it => it.proyectoNombre)?.proyectoNombre
          }
          items={
            historyPrintTarget
              ? (historyPrintTarget.itemsRecibidos || []).map((it: any) => ({
                  ...it,
                  cantidadRecibida: it.cantidadRecibidaHoy,
                  unidadMedida: it.unidadMedida || 'und',
                  rotularEtiqueta: true
                }))
              : itemsToReceive
          }
          missingItems={historyPrintTarget?.itemsFaltantes || receptionComplete?.nriSnapshot?.itemsFaltantes}
          rejectedItems={historyPrintTarget?.itemsRechazados || receptionComplete?.nriSnapshot?.itemsRechazados}
          onClose={() => {
            setShowPrintEntryModal(false);
            setHistoryPrintTarget(null);
          }}
        />
      )}

      {/* Impresión Bimodal: Acta Formal de Rechazo y Devolución (PLAN-040 / PLAN-046) */}
      {showPrintRejectionModal && (
        <PrintSheetRampRejection
          nriFolio={historyPrintTarget?.nriFolio || receptionComplete?.nriFolio}
          actaRechazoFolio={historyPrintTarget?.actaRechazoFolio || receptionComplete?.actaRechazoFolio || undefined}
          folioOAB={historyPrintTarget?.folioOAB || receptionComplete?.folioOAB || selectedFolio || 'OAB-RAMPA'}
          proveedor={
            historyPrintTarget?.proveedor ||
            subOrdenesList.find(s => s.folio === activeSubOrderFolio)?.proveedor ||
            pendingOrders.find(o => o.folio === selectedFolio)?.proveedor
          }
          numeroNotaEntrega={historyPrintTarget?.numeroNotaEntrega || historyPrintTarget?.numeroGuia || notaEntrega}
          fechaRecepcion={historyPrintTarget?.fechaRecepcion || fechaRecepcion}
          receptorNombre={historyPrintTarget?.receptorNombre || historyPrintTarget?.receptorRampa || 'Operador de Rampa'}
          choferNombre={historyPrintTarget?.choferNombre || historyPrintTarget?.choferPlaca || choferNombre || undefined}
          placaVehiculo={historyPrintTarget?.placaVehiculo || placaVehiculo || undefined}
          itemsRechazados={
            historyPrintTarget
              ? (historyPrintTarget.itemsRechazados || []).map((it: any) => ({
                  codigo: it.codigo || it.sku,
                  nombre: it.nombre,
                  cantidadRechazada: it.cantidadRechazada || 0,
                  unidad: it.unidadMedida || 'und',
                  motivo: it.motivo || 'Rechazado en inspección física en rampa'
                }))
              : itemsToReceive
                  .filter(it => (it.cantidadRechazada || 0) > 0)
                  .map(it => ({
                    codigo: it.codigo,
                    nombre: it.nombre,
                    cantidadRechazada: it.cantidadRechazada || 0,
                    unidad: it.unidadMedida || 'und',
                    motivo: it.notasDiscrepancia || 'Rechazado en inspección física en rampa'
                  }))
          }
          onClose={() => {
            setShowPrintRejectionModal(false);
            setHistoryPrintTarget(null);
          }}
        />
      )}

      {/* Asistente Ergonómico de Conversión de Unidades UoM en Muelle (PLAN-042 / GAP-041-02) */}
      {uomModalItemIdx !== null && itemsToReceive[uomModalItemIdx] && (
        <UomConversionModal
          isOpen={uomModalItemIdx !== null}
          onClose={() => setUomModalItemIdx(null)}
          itemName={itemsToReceive[uomModalItemIdx].nombre}
          itemUnit={itemsToReceive[uomModalItemIdx].unidadMedida || 'und'}
          currentReceivedQty={itemsToReceive[uomModalItemIdx].cantidadRecibidaHoy ?? itemsToReceive[uomModalItemIdx].cantidadRecibida ?? 0}
          approvedQty={itemsToReceive[uomModalItemIdx].cantidadAprobada ?? itemsToReceive[uomModalItemIdx].cantidadSolicitada ?? 0}
          onApplyConversion={handleApplyUomConversion}
        />
      )}
    </div>
  );
};
